import React, { useState, useEffect } from "react";
import { useRef } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Space,
  message,
  Alert,
  List,
  Popconfirm,
  Upload,
  Tabs,
  Tag,
  Modal,
  Tooltip,
  Divider,
  Switch,
  Select,
  InputNumber,
} from "antd";
import {
  SaveOutlined,
  KeyOutlined,
  FolderAddOutlined,
  UploadOutlined,
  TagOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  HddOutlined,
  EditOutlined,
  DeleteOutlined,
  RobotOutlined,
  LinkOutlined,
  LikeOutlined,
  DislikeOutlined,
  CloudDownloadOutlined,
  LikeFilled,
  DislikeFilled,
  StarOutlined,
  StarFilled,
  BellOutlined,
  TeamOutlined,
  PlusOutlined,
  UserAddOutlined,
  CrownOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  buildKnowledgeBundle,
  normalizeKnowledgeBundle,
} from "../services/knowledgeSync";
import {
  saveKnowledgeImage,
  deleteKnowledgeImage,
  getKnowledgeImageStats,
  saveBrandAsset,
  deleteBrandAsset,
  getBrandAssetStats,
  saveAnalysisDoc,
  deleteAnalysisDoc,
  getAnalysisDocStats,
  saveAdCopyDoc,
  deleteAdCopyDoc,
  getAdCopyDocStats,
  saveBlogDoc,
  deleteBlogDoc,
  getBlogDocStats,
  savePostExampleImage,
  deletePostExampleImage,
  savePostTypeDoc,
  deletePostTypeDoc,
} from "../services/knowledgeStore";
import AIService from "../services/AIService";
import { getModelStats } from "../services/modelStats";
import { showErrorWithCopy, showWorkspaceError } from "../utils/errorNotice.jsx";
import { usePasteFiles } from "../hooks/usePasteFiles";
import {
  fetchWorkspaces,
  fetchWorkspaceDetails as fetchWorkspaceDetailsApi,
  createWorkspace,
  updateWorkspaceSettings,
  saveWorkspaceApiKeys,
  inviteWorkspaceMember,
  revokeWorkspaceInvite,
  updateWorkspaceMember,
  removeWorkspaceMember,
  exportWorkspaceData,
  deleteWorkspace,
  fetchPendingInvites,
  acceptWorkspaceInvite,
  rejectWorkspaceInvite,
} from "../services/workspaces";
import { saveUserApiKeys } from "../services/userSettingsStore";
import { updateProfile } from "../services/profile";
import {
  DEFAULT_ANALYSIS_MODEL,
  DEFAULT_BLOG_MODEL,
  DEFAULT_POST_ANALYSIS_MODEL,
  TEXT_MODEL_OPTIONS,
  getModelLabel,
  normalizeModelSelection,
} from "../services/modelCatalog";

const { Title, Text } = Typography;
const API_KEY_FIELDS = ["openai", "gemini", "claude", "huggingface", "tribe_server_url", "tribe_api_key", "atlascloud", "kling"];

const POST_EXAMPLE_TAG_OPTIONS = [
  { value: "high performing", label: "High performing" },
  { value: "low performing", label: "Low performing" },
  { value: "high cvr", label: "High CVR" },
  { value: "low cvr", label: "Low CVR" },
  { value: "high watch thru rate", label: "High watch thru rate" },
  { value: "low watch thru rate", label: "Low watch thru rate" },
  { value: "high ctr", label: "High CTR" },
  { value: "low ctr", label: "Low CTR" },
  { value: "high str", label: "High STR" },
  { value: "low str", label: "Low STR" },
  { value: "high spend", label: "High spend" },
  { value: "low spend", label: "Low spend" },
  { value: "high roas", label: "High ROAS" },
  { value: "low roas", label: "Low ROAS" },
];

const ANALYSIS_UPLOAD_LIMITS = {
  maxFilesPerBatch: 50,
  maxTotalDocs: 500,
  maxFileSizeBytes: 50 * 1024 * 1024,
  maxConcurrent: 5,
};
const ANALYSIS_ALLOWED_EXTENSIONS = new Set([
  ".pdf",
  ".txt",
  ".md",
  ".json",
  ".csv",
  ".doc",
  ".docx",
]);

const SettingsPage = ({
  knowledgeDb = [],
  onKnowledgeChange,
  tags = [],
  onTagsChange,
  postTypeTags = [],
  onPostTypeTagsChange,
  postExamples = [],
  onPostExamplesChange,
  brandLibrary,
  onBrandChange,
  onKnowledgeExtrasChange,
  aiProfile,
  onAiProfileChange,
  analysisKnowledge,
  onAnalysisKnowledgeChange,
  adCopyKnowledge,
  onAdCopyKnowledgeChange,
  blogKnowledge,
  onBlogKnowledgeChange,
  blogSeo,
  onBlogSeoChange,
  apiKeys,
  apiKeyStatus = null,
  onApiKeysChange,
  onApiKeyStatusChange,
  analysisModel,
  postAnalysisModel,
  onPostAnalysisModelChange,
  blogModel,
  onBlogModelChange,
  modelStats,
  activityPreferences,
  onActivityPreferencesChange,
  notificationPreferences,
  notificationPermission = "unsupported",
  onNotificationPreferencesChange,
  onRequestNotificationPermission,
  onActiveWorkspaceChange,
  workspaceApiKeys = null,
  workspaceApiKeyStatus = null,
  workspaceShareApiKeys = false,
  onWorkspaceApiKeysChange,
  onWorkspaceApiKeyStatusChange,
  onWorkspaceShareApiKeysChange,
  syncStatus,
  disabled = false,
  googleDriveConnected = false,
  onGoogleDriveDisconnect,
}) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  // Internal tool: all workspace/team features are always available.
  const isBusinessTier = true;
  const resolvedNotificationPreferences = {
    completionAudio: true,
    completionPush: false,
    pushPermissionAsked: false,
    ...(notificationPreferences || {}),
  };
  const notificationSupport =
    typeof window !== "undefined" && typeof Notification !== "undefined";
  const effectiveNotificationPermission = notificationSupport
    ? notificationPermission || Notification.permission
    : "unsupported";
  const handleToggleCompletionAudio = (enabled) => {
    onNotificationPreferencesChange?.({ completionAudio: Boolean(enabled) });
  };
  const handleToggleCompletionPush = async (enabled) => {
    if (!enabled) {
      onNotificationPreferencesChange?.({
        completionPush: false,
        pushPermissionAsked: true,
      });
      return;
    }
    if (!notificationSupport) {
      messageApi.warning("Desktop notifications are not supported in this browser.");
      return;
    }
    if (effectiveNotificationPermission === "granted") {
      onNotificationPreferencesChange?.({
        completionPush: true,
        pushPermissionAsked: true,
      });
      return;
    }
    const permission = onRequestNotificationPermission
      ? await onRequestNotificationPermission()
      : await Notification.requestPermission();
    const granted = permission === "granted";
    onNotificationPreferencesChange?.({
      completionPush: granted,
      pushPermissionAsked: true,
    });
    if (!granted) {
      messageApi.info(
        "Desktop notifications were not enabled. You can allow them later in your browser settings.",
      );
    }
  };
  const postTypeFormatOptions = [
    { value: "meta_ads", label: "Meta Ads (5 headlines + 5 primary texts)" },
    { value: "organic", label: "Organic (1-2 sentences + 3 hashtag bundles)" },
    { value: "blog", label: "Blog Post" },
    { value: "native_story", label: "Native Story" },
  ];
  const formatLabelMap = postTypeFormatOptions.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});
  const normalizePostTypeFormat = (value) => {
    const raw = String(value || "")
      .trim()
      .toLowerCase();
    if (
      raw === "blog" ||
      raw === "native_story" ||
      raw === "meta_ads" ||
      raw === "organic"
    ) {
      return raw;
    }
    return "meta_ads";
  };
  const normalizePostExampleTags = (values) =>
    Array.from(
      new Set(
        (values || [])
          .map((tag) => String(tag || "").trim())
          .filter(Boolean),
      ),
    );
  const [folderName, setFolderName] = useState("");
  const [folderNotes, setFolderNotes] = useState("");
  const [folderImages, setFolderImages] = useState([]);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editName, setEditName] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editImages, setEditImages] = useState([]);
  const [editAddedIds, setEditAddedIds] = useState([]);
  const [editRemovedIds, setEditRemovedIds] = useState([]);
  const [tagName, setTagName] = useState("");
  const [tagDescription, setTagDescription] = useState("");
  const [editingTag, setEditingTag] = useState(null);
  const [editTagName, setEditTagName] = useState("");
  const [editTagDescription, setEditTagDescription] = useState("");
  const [postTypeTagName, setPostTypeTagName] = useState("");
  const [postTypeTagDescription, setPostTypeTagDescription] = useState("");
  const [postTypeTagExtraInfo, setPostTypeTagExtraInfo] = useState("");
  const [postTypeTagFormat, setPostTypeTagFormat] = useState("meta_ads");
  const [postTypeTagDocs, setPostTypeTagDocs] = useState([]);
  const [editingPostTypeTag, setEditingPostTypeTag] = useState(null);
  const [editPostTypeTagName, setEditPostTypeTagName] = useState("");
  const [editPostTypeTagDescription, setEditPostTypeTagDescription] =
    useState("");
  const [editPostTypeTagExtraInfo, setEditPostTypeTagExtraInfo] = useState("");
  const [editPostTypeTagFormat, setEditPostTypeTagFormat] =
    useState("meta_ads");
  const [editPostTypeTagDocs, setEditPostTypeTagDocs] = useState([]);
  const [editPostTypeTagAddedIds, setEditPostTypeTagAddedIds] = useState([]);
  const [editPostTypeTagRemovedIds, setEditPostTypeTagRemovedIds] = useState(
    [],
  );
  const [postExampleName, setPostExampleName] = useState("");
  const [postExampleNotes, setPostExampleNotes] = useState("");
  const [postExampleImages, setPostExampleImages] = useState([]);
  const [postExampleTypeId, setPostExampleTypeId] = useState("");
  const [postExampleTags, setPostExampleTags] = useState([]);
  const [editingPostExampleFolder, setEditingPostExampleFolder] =
    useState(null);
  const [editPostExampleName, setEditPostExampleName] = useState("");
  const [editPostExampleNotes, setEditPostExampleNotes] = useState("");
  const [editPostExampleImages, setEditPostExampleImages] = useState([]);
  const [editPostExampleAddedIds, setEditPostExampleAddedIds] = useState([]);
  const [editPostExampleRemovedIds, setEditPostExampleRemovedIds] = useState(
    [],
  );
  const [editPostExampleTypeId, setEditPostExampleTypeId] = useState("");
  const [editPostExampleTags, setEditPostExampleTags] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);
  const [newColor, setNewColor] = useState("");
  const [newFont, setNewFont] = useState("");
  const brandAssetPasteRef = useRef(null);
  const folderImagesPasteRef = useRef(null);
  const editImagesPasteRef = useRef(null);
  const analysisUploadLockRef = useRef(false);
  const analysisDocsPasteRef = useRef(null);
  const adCopyDocsPasteRef = useRef(null);
  const blogDocsPasteRef = useRef(null);
  const postTypeDocsPasteRef = useRef(null);
  const postTypeEditDocsPasteRef = useRef(null);
  const postExamplePasteRef = useRef(null);
  const postExampleEditPasteRef = useRef(null);
  const [aiProfileState, setAiProfileState] = useState(
    aiProfile || {
      instructions: "",
      personality: "",
      mustDo: "",
      avoid: "",
      referenceUrls: [],
    },
  );
  const aiProfileSaveRef = useRef(null);
  const brandSaveRef = useRef(null);
  const [aiUrlInput, setAiUrlInput] = useState("");
  const [analysisNotes, setAnalysisNotes] = useState(
    analysisKnowledge?.notes || "",
  );
  const [analysisDocs, setAnalysisDocs] = useState(
    analysisKnowledge?.docs || [],
  );
  const [analysisUploadState, setAnalysisUploadState] = useState({
    active: false,
    total: 0,
    completed: 0,
    failed: 0,
    skipped: 0,
  });
  const [adCopy, setAdCopy] = useState(
    adCopyKnowledge || {
      notes: "",
      avoid: "",
      docs: [],
      headlines: Array.from({ length: 5 }, () => ""),
      primaryTexts: Array.from({ length: 5 }, () => ""),
    },
  );
  const [blogGuide, setBlogGuide] = useState(
    blogKnowledge || {
      notes: "",
      goals: "",
      audience: "",
      mustInclude: "",
      avoid: "",
      cta: "",
      docs: [],
    },
  );
  const [blogSeoDraft, setBlogSeoDraft] = useState(
    blogSeo || {
      metaTitle: "",
      metaDescription: "",
      keywords: [],
      slug: "",
      canonicalUrl: "",
      schemaMarkup: "",
    },
  );
  const [storageStats, setStorageStats] = useState({
    localBytes: 0,
    imageBytes: 0,
    imageCount: 0,
    brandBytes: 0,
    brandCount: 0,
    analysisBytes: 0,
    analysisCount: 0,
    adCopyBytes: 0,
    adCopyCount: 0,
    blogBytes: 0,
    blogCount: 0,
    quotaBytes: null,
    usageBytes: null,
  });
  const [editingBrandAsset, setEditingBrandAsset] = useState(null);
  const [brandAssetBaseNameDraft, setBrandAssetBaseNameDraft] = useState("");
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthReport, setHealthReport] = useState([]);
  const [importing, setImporting] = useState(false);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceList, setWorkspaceList] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [workspaceInvites, setWorkspaceInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("viewer");
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [workspaceProfile, setWorkspaceProfile] = useState(null);
  const isWorkspaceReady = Boolean(activeWorkspaceId);
  const [workspaceSettingsDraft, setWorkspaceSettingsDraft] = useState({
    domainJoinEnabled: false,
    domainJoinDomain: "",
    shareApiKeys: false,
    isPersonal: false,
    allowAdminManagePermissions: true,
    allowAdminManageLimits: true,
  });
  const [workspaceKeysDraft, setWorkspaceKeysDraft] = useState({
    openai: "",
    gemini: "",
    claude: "",
    huggingface: "",
    tribe_server_url: "",
    tribe_api_key: "",
    atlascloud: "",
    kling: "",
  });
  const [workspaceSettingsSaving, setWorkspaceSettingsSaving] = useState(false);
  const [workspaceKeysSaving, setWorkspaceKeysSaving] = useState(false);
  const [personalKeysSaving, setPersonalKeysSaving] = useState(false);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [permissionsTarget, setPermissionsTarget] = useState(null);
  const [permissionsDraft, setPermissionsDraft] = useState({});
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const [pendingInvitesForUser, setPendingInvitesForUser] = useState([]);

  const emptyApiKeys = {
    openai: "",
    gemini: "",
    claude: "",
    openrouter: "",
    huggingface: "",
    tribe_server_url: "",
    tribe_api_key: "",
    atlascloud: "",
  };
  const resolvedApiKeyStatus = API_KEY_FIELDS.reduce((acc, field) => {
    acc[field] = Boolean(apiKeyStatus?.[field]);
    return acc;
  }, {});
  const resolvedWorkspaceApiKeyStatus = API_KEY_FIELDS.reduce((acc, field) => {
    acc[field] = Boolean(workspaceApiKeyStatus?.[field]);
    return acc;
  }, {});

  const brandStored = brandLibrary || {
    name: "",
    message: "",
    colors: [],
    fonts: [],
    assets: [],
  };

  const [brandDraft, setBrandDraft] = useState(brandStored);
  const [activityLibrary, setActivityLibrary] = useState(
    activityPreferences || [],
  );
  const [modelStatsState, setModelStatsState] = useState(
    modelStats || getModelStats(),
  );
  const env = import.meta.env || {};
  const roleOptions = [
    { value: "owner", label: "Owner" },
    { value: "admin", label: "Admin" },
    { value: "editor", label: "Editor" },
    { value: "viewer", label: "Viewer" },
  ];
  const rolePermissions = {
    owner: "Full control, billing, invites, and permissions.",
    admin: "Manage members, invites, and settings.",
    editor: "Edit knowledge and run generation.",
    viewer: "Read-only access.",
  };
  const permissionCatalog = [
    {
      key: "can_edit_knowledge",
      label: "Edit shared library",
      detail: "Update AI instructions, tags, knowledge, and references.",
    },
    {
      key: "can_invite_members",
      label: "Invite members",
      detail: "Send workspace invites to new teammates.",
    },
    {
      key: "can_manage_members",
      label: "Manage members",
      detail: "Remove members from the workspace.",
    },
    {
      key: "can_manage_permissions",
      label: "Manage permissions",
      detail: "Change roles and granular access for teammates.",
    },
  ];
  const rolePermissionDefaults = {
    owner: {
      can_edit_knowledge: true,
      can_invite_members: true,
      can_manage_members: true,
      can_manage_permissions: true,
    },
    admin: {
      can_edit_knowledge: true,
      can_invite_members: true,
      can_manage_members: true,
      can_manage_permissions: true,
    },
    editor: {
      can_edit_knowledge: true,
      can_invite_members: false,
      can_manage_members: false,
      can_manage_permissions: false,
    },
    viewer: {
      can_edit_knowledge: false,
      can_invite_members: false,
      can_manage_members: false,
      can_manage_permissions: false,
    },
  };

  useEffect(() => {
    setBrandDraft(brandStored);
  }, [brandLibrary]);

  useEffect(() => {
    setAiProfileState(
      aiProfile || {
        instructions: "",
        personality: "",
        mustDo: "",
        avoid: "",
        referenceUrls: [],
      },
    );
  }, [aiProfile]);

  useEffect(() => {
    setAnalysisNotes(analysisKnowledge?.notes || "");
    setAnalysisDocs(analysisKnowledge?.docs || []);
  }, [analysisKnowledge]);

  useEffect(() => {
    setAdCopy(
      adCopyKnowledge || {
        notes: "",
        avoid: "",
        docs: [],
        headlines: Array.from({ length: 5 }, () => ""),
        primaryTexts: Array.from({ length: 5 }, () => ""),
      },
    );
  }, [adCopyKnowledge]);

  useEffect(() => {
    setBlogGuide(
      blogKnowledge || {
        notes: "",
        goals: "",
        audience: "",
        mustInclude: "",
        avoid: "",
        cta: "",
        docs: [],
      },
    );
  }, [blogKnowledge]);

  useEffect(() => {
    setBlogSeoDraft(
      blogSeo || {
        metaTitle: "",
        metaDescription: "",
        keywords: [],
        slug: "",
        canonicalUrl: "",
        schemaMarkup: "",
      },
    );
  }, [blogSeo]);

  useEffect(() => {
    setActivityLibrary(activityPreferences || []);
  }, [activityPreferences]);

  useEffect(() => {
    setModelStatsState(modelStats || getModelStats());
  }, [modelStats]);

  const refreshActivityLibrary = () => {
    setActivityLibrary(activityPreferences || []);
  };

  useEffect(() => {
    refreshActivityLibrary();
  }, [activityPreferences]);

  const requireWorkspace = (actionLabel) => {
    if (!activeWorkspaceId) {
      messageApi.error(
        actionLabel
          ? `${actionLabel} requires an active workspace.`
          : "Select or create a workspace first.",
      );
      return false;
    }
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return false;
    }
    return true;
  };

  const fetchWorkspaceData = async () => {
    setWorkspaceLoading(true);
    try {
      const payload = await fetchWorkspaces();
      const profile = payload?.profile || null;
      const list = payload?.workspaces || [];
      setWorkspaceProfile(profile || { id: "", email: "" });
      setWorkspaceList(list);
      const resolvedActive =
        profile?.active_workspace_id || list[0]?.id || null;
      setActiveWorkspaceId(resolvedActive);
      if (resolvedActive) {
        onActiveWorkspaceChange?.(resolvedActive);
      }
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to load workspaces",
        error,
        "Settings > Workspaces > Load workspaces",
      );
    } finally {
      setWorkspaceLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaceData();
    loadPendingInvites();
  }, []);

  const fetchWorkspaceDetails = async (workspaceId) => {
    if (!workspaceId) return;
    setWorkspaceLoading(true);
    try {
      const payload = await fetchWorkspaceDetailsApi(workspaceId);
      setWorkspaceMembers(payload?.members || []);
      setWorkspaceInvites(payload?.invites || []);
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to load workspace members",
        error,
        "Settings > Workspaces > Load workspace details",
      );
    } finally {
      setWorkspaceLoading(false);
    }
  };

  useEffect(() => {
    if (activeWorkspaceId) {
      fetchWorkspaceDetails(activeWorkspaceId);
    }
  }, [activeWorkspaceId]);

  const currentWorkspace = workspaceList.find(
    (item) => item.id === activeWorkspaceId,
  );
  const isWorkspaceOwner = currentWorkspace?.role === "owner";
  const canManageWorkspace = ["owner", "admin"].includes(
    currentWorkspace?.role || "",
  );
  const canEditWorkspace = ["owner", "admin", "editor"].includes(
    currentWorkspace?.role || "",
  );
  const normalizePermissionOverrides = (permissions) => {
    const normalized = {};
    Object.entries(permissions || {}).forEach(([key, value]) => {
      if (typeof value === "boolean") {
        normalized[key] = value;
      }
    });
    return normalized;
  };
  const resolveMemberPermissions = (member) => {
    const role = member?.role || currentWorkspace?.role || "viewer";
    const base = {
      ...(rolePermissionDefaults[role] || rolePermissionDefaults.viewer),
    };
    if (role === "admin") {
      base.can_manage_permissions =
        currentWorkspace?.allowAdminManagePermissions ?? true;
    }
    const overrides = normalizePermissionOverrides(member?.permissions);
    return { ...base, ...overrides };
  };
  const currentMember =
    workspaceMembers.find(
      (member) => member.user_id === workspaceProfile?.id,
    ) || null;
  const currentPermissions = resolveMemberPermissions(currentMember);
  const canEditKnowledge = Boolean(currentPermissions.can_edit_knowledge);
  const canInviteMembers = Boolean(currentPermissions.can_invite_members);
  const canManageMembers = Boolean(currentPermissions.can_manage_members);
  const canManagePermissions = Boolean(
    currentPermissions.can_manage_permissions,
  );
  const canEditMemberSettings = canManagePermissions;
  const memberRoleOptions = isWorkspaceOwner
    ? roleOptions
    : roleOptions.filter((item) => item.value !== "owner");

  useEffect(() => {
    if (!canEditKnowledge) return;
    if (aiProfileSaveRef.current) {
      clearTimeout(aiProfileSaveRef.current);
    }
    aiProfileSaveRef.current = setTimeout(() => {
      onAiProfileChange?.(aiProfileState);
      onKnowledgeExtrasChange?.();
    }, 600);
    return () => {
      if (aiProfileSaveRef.current) {
        clearTimeout(aiProfileSaveRef.current);
      }
    };
  }, [
    aiProfileState,
    canEditKnowledge,
    onAiProfileChange,
    onKnowledgeExtrasChange,
  ]);

  // Auto-save brand library changes (debounced)
  useEffect(() => {
    if (!canEditKnowledge) return;
    if (brandSaveRef.current) {
      clearTimeout(brandSaveRef.current);
    }
    brandSaveRef.current = setTimeout(() => {
      onBrandChange?.(brandDraft);
      onKnowledgeExtrasChange?.();
    }, 600);
    return () => {
      if (brandSaveRef.current) {
        clearTimeout(brandSaveRef.current);
      }
    };
  }, [brandDraft, canEditKnowledge, onBrandChange, onKnowledgeExtrasChange]);

  useEffect(() => {
    if (!currentWorkspace) return;
    setWorkspaceSettingsDraft({
      domainJoinEnabled: Boolean(currentWorkspace.domainJoinEnabled),
      domainJoinDomain: currentWorkspace.domainJoinDomain || "",
      shareApiKeys: Boolean(currentWorkspace.shareApiKeys),
      isPersonal: Boolean(currentWorkspace.isPersonal),
      allowAdminManagePermissions:
        currentWorkspace.allowAdminManagePermissions ?? true,
      allowAdminManageLimits: currentWorkspace.allowAdminManageLimits ?? true,
    });
  }, [
    currentWorkspace?.id,
    currentWorkspace?.domainJoinEnabled,
    currentWorkspace?.domainJoinDomain,
    currentWorkspace?.shareApiKeys,
    currentWorkspace?.isPersonal,
    currentWorkspace?.allowAdminManagePermissions,
    currentWorkspace?.allowAdminManageLimits,
  ]);

  useEffect(() => {
    setWorkspaceKeysDraft({
      openai: workspaceApiKeys?.openai || "",
      gemini: workspaceApiKeys?.gemini || "",
      claude: workspaceApiKeys?.claude || "",
      huggingface: workspaceApiKeys?.huggingface || "",
      tribe_server_url: workspaceApiKeys?.tribe_server_url || "",
      tribe_api_key: workspaceApiKeys?.tribe_api_key || "",
      atlascloud: workspaceApiKeys?.atlascloud || "",
      kling: workspaceApiKeys?.kling || "",
    });
  }, [workspaceApiKeys, activeWorkspaceId]);

  const handleSetActiveWorkspace = async (workspaceId) => {
    setActiveWorkspaceId(workspaceId);
    try {
      const payload = await updateProfile({
        active_workspace_id: workspaceId,
      });
      if (payload?.profile) {
        setWorkspaceProfile(payload.profile);
      }
    } catch (error) {
      // ignore
    }
    onActiveWorkspaceChange?.(workspaceId);
  };

  const getEmailDomain = (email) => {
    const raw = String(email || "").trim();
    const atIndex = raw.lastIndexOf("@");
    if (atIndex <= 0 || atIndex === raw.length - 1) return "";
    return raw.slice(atIndex + 1).toLowerCase();
  };

  const handleToggleDomainJoin = (enabled) => {
    const fallbackDomain = getEmailDomain(workspaceProfile?.email);
    setWorkspaceSettingsDraft((prev) => ({
      ...prev,
      domainJoinEnabled: enabled,
      domainJoinDomain:
        enabled && !prev.domainJoinDomain
          ? fallbackDomain
          : prev.domainJoinDomain,
    }));
  };

  const normalizeApiKeyUpdates = (source = {}) =>
    API_KEY_FIELDS.reduce((acc, field) => {
      const value = String(source?.[field] || "").trim();
      if (value) {
        acc[field] = value;
      }
      return acc;
    }, {});

  const mergeApiKeyStatus = (current = {}, updates = {}, clear = []) =>
    API_KEY_FIELDS.reduce((acc, field) => {
      if (clear.includes(field)) {
        acc[field] = false;
      } else if (updates[field]) {
        acc[field] = true;
      } else {
        acc[field] = Boolean(current?.[field]);
      }
      return acc;
    }, {});

  const handleSaveWorkspaceSettings = async () => {
    if (!activeWorkspaceId) {
      messageApi.error("Select a workspace first.");
      return;
    }
    if (!canManageWorkspace) {
      messageApi.error("You need owner or admin permissions.");
      return;
    }
    const domain = workspaceSettingsDraft.domainJoinEnabled
      ? workspaceSettingsDraft.domainJoinDomain.trim().toLowerCase()
      : "";
    if (workspaceSettingsDraft.domainJoinEnabled && !domain) {
      messageApi.error("Domain is required when auto-join is enabled.");
      return;
    }
    setWorkspaceSettingsSaving(true);
    try {
      const adminControlUpdates = isWorkspaceOwner
        ? {
            allow_admin_manage_permissions: Boolean(
              workspaceSettingsDraft.allowAdminManagePermissions,
            ),
            allow_admin_manage_limits: Boolean(
              workspaceSettingsDraft.allowAdminManageLimits,
            ),
          }
        : {};
      await updateWorkspaceSettings(activeWorkspaceId, {
        domain_join_enabled: Boolean(workspaceSettingsDraft.domainJoinEnabled),
        domain_join_domain: domain || null,
        ...adminControlUpdates,
      });
      messageApi.success("Workspace settings saved");
      await fetchWorkspaceData();
      fetchWorkspaceDetails(activeWorkspaceId);
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to save workspace settings",
        error,
        "Settings > Workspaces > Save workspace settings",
      );
    } finally {
      setWorkspaceSettingsSaving(false);
    }
  };

  const handleSaveWorkspaceApiKeys = async (overrideData) => {
    if (!activeWorkspaceId) {
      messageApi.error("Select a workspace first.");
      return;
    }
    if (!canManageWorkspace) {
      messageApi.error("You need owner or admin permissions.");
      return;
    }
    setWorkspaceKeysSaving(true);
    try {
      const hasExplicitOverride =
        overrideData &&
        typeof overrideData === "object" &&
        API_KEY_FIELDS.some(
          (field) => typeof overrideData?.[field] === "string",
        );
      const draftData = hasExplicitOverride ? overrideData : {
        openai: workspaceKeysDraft.openai || "",
        gemini: workspaceKeysDraft.gemini || "",
        claude: workspaceKeysDraft.claude || "",
        huggingface: workspaceKeysDraft.huggingface || "",
        tribe_server_url: workspaceKeysDraft.tribe_server_url || "",
        tribe_api_key: workspaceKeysDraft.tribe_api_key || "",
        atlascloud: workspaceKeysDraft.atlascloud || "",
        kling: workspaceKeysDraft.kling || "",
      };
      const updates = normalizeApiKeyUpdates(draftData);
      if (!Object.keys(updates).length) {
        messageApi.info("Enter a new key to rotate it.");
        return;
      }
      const payload = await saveWorkspaceApiKeys(activeWorkspaceId, updates, []);
      const nextStatus =
        payload?.status || mergeApiKeyStatus(resolvedWorkspaceApiKeyStatus, updates, []);
      onWorkspaceApiKeysChange?.({ ...emptyApiKeys });
      onWorkspaceApiKeyStatusChange?.(nextStatus);
      setWorkspaceKeysDraft({ ...emptyApiKeys });
      messageApi.success("Workspace API keys saved");
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to save workspace API keys",
        error,
        "Settings > API Keys > Save workspace keys",
      );
    } finally {
      setWorkspaceKeysSaving(false);
    }
  };

  const handleClearWorkspaceApiKeys = async () => {
    if (!activeWorkspaceId) {
      messageApi.error("Select a workspace first.");
      return;
    }
    if (!canManageWorkspace) {
      messageApi.error("You need owner or admin permissions.");
      return;
    }
    setWorkspaceKeysSaving(true);
    try {
      const payload = await saveWorkspaceApiKeys(activeWorkspaceId, {}, API_KEY_FIELDS);
      const nextStatus =
        payload?.status ||
        mergeApiKeyStatus(resolvedWorkspaceApiKeyStatus, {}, API_KEY_FIELDS);
      onWorkspaceApiKeysChange?.({ ...emptyApiKeys });
      onWorkspaceApiKeyStatusChange?.(nextStatus);
      setWorkspaceKeysDraft({ ...emptyApiKeys });
      messageApi.success("Workspace API keys cleared");
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to clear workspace API keys",
        error,
        "Settings > API Keys > Clear workspace keys",
      );
    } finally {
      setWorkspaceKeysSaving(false);
    }
  };

  const handleCreateWorkspace = async () => {
    const name = newWorkspaceName.trim();
    if (!name) {
      message.error("Workspace name is required");
      return;
    }
    try {
      const payload = await createWorkspace(name);
      const workspace = payload?.workspace;
      if (!workspace?.id) throw new Error("Workspace creation failed");
      setNewWorkspaceName("");
      message.success("Workspace created");
      await fetchWorkspaceData();
      setActiveWorkspaceId(workspace.id);
      onActiveWorkspaceChange?.(workspace.id);
      fetchWorkspaceDetails(workspace.id);
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to create workspace",
        error,
        "Settings > Workspaces > Create workspace",
      );
    }
  };

  const handleInviteMember = async () => {
    const email = inviteEmail.trim();
    if (!email) {
      message.error("Invite email is required");
      return;
    }
    if (!activeWorkspaceId) {
      message.error("Select a workspace first");
      return;
    }
    if (!canInviteMembers) {
      message.error("You do not have permission to invite members.");
      return;
    }
    try {
      await inviteWorkspaceMember(activeWorkspaceId, email, inviteRole);
      setInviteEmail("");
      message.success("Invite created");
      fetchWorkspaceDetails(activeWorkspaceId);
    } catch (error) {
      const errorData = error?.response ? await error.response.json() : { message: error?.message };
      showWorkspaceError(messageApi, errorData);
    }
  };

  const handleUpdateMemberRole = async (memberId, role) => {
    if (!canManagePermissions) {
      message.error("You do not have permission to change roles.");
      return;
    }
    if (role === "owner" && !isWorkspaceOwner) {
      message.error("Only the workspace owner can assign the owner role.");
      return;
    }
    try {
      await updateWorkspaceMember(activeWorkspaceId, memberId, { role });
      message.success("Role updated");
      fetchWorkspaceDetails(activeWorkspaceId);
    } catch (error) {
      const errorData = error?.response ? await error.response.json() : { message: error?.message || "Failed to update role" };
      showWorkspaceError(messageApi, errorData);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!canManageMembers) {
      message.error("You do not have permission to manage members.");
      return;
    }
    try {
      await removeWorkspaceMember(activeWorkspaceId, memberId);
      message.success("Member removed");
      fetchWorkspaceDetails(activeWorkspaceId);
    } catch (error) {
      const errorData = error?.response ? await error.response.json() : { message: error?.message || "Failed to remove member" };
      showWorkspaceError(messageApi, errorData);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!canInviteMembers) {
      message.error("You do not have permission to manage invites.");
      return;
    }
    try {
      await revokeWorkspaceInvite(activeWorkspaceId, inviteId);
      message.success("Invite revoked");
      fetchWorkspaceDetails(activeWorkspaceId);
    } catch (error) {
      const errorData = error?.response ? await error.response.json() : { message: error?.message || "Failed to revoke invite" };
      showWorkspaceError(messageApi, errorData);
    }
  };

  const handleExportWorkspaceData = async () => {
    if (!activeWorkspaceId) {
      messageApi.error("Select a workspace first.");
      return;
    }
    if (!isWorkspaceOwner) {
      messageApi.error("Only workspace owners can export workspace data.");
      return;
    }
    try {
      const data = await exportWorkspaceData(activeWorkspaceId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workspace-${activeWorkspaceId}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      messageApi.success("Workspace data exported successfully");
    } catch (error) {
      messageApi.error(error?.message || "Failed to export workspace data");
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!activeWorkspaceId) {
      messageApi.error("Select a workspace first.");
      return;
    }
    if (!isWorkspaceOwner) {
      messageApi.error("Only workspace owners can delete workspaces.");
      return;
    }

    Modal.confirm({
      title: 'Delete Workspace',
      content: (
        <div>
          <p>Are you sure you want to delete this workspace?</p>
          <p><strong>This action cannot be undone!</strong></p>
          <p>All workspace data including members, invites, and settings will be permanently deleted.</p>
          <p>We recommend exporting your workspace data before deletion.</p>
        </div>
      ),
      okText: 'Delete Workspace',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteWorkspace(activeWorkspaceId);
          messageApi.success("Workspace deleted successfully");
          // Refresh workspace list
          await fetchWorkspaces();
          // Clear active workspace
          setActiveWorkspaceId(null);
        } catch (error) {
          const errorData = error?.response ? await error.response.json() : { message: error?.message || "Failed to delete workspace" };
          showWorkspaceError(messageApi, errorData);
        }
      }
    });
  };

  const loadPendingInvites = async () => {
    try {
      const { invites } = await fetchPendingInvites();
      setPendingInvitesForUser(invites || []);
    } catch (error) {
      console.error("Failed to load pending invites:", error);
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      await acceptWorkspaceInvite(inviteId);
      messageApi.success("Workspace invite accepted!");
      await loadPendingInvites();
      // Reload workspaces to show the new workspace
      window.location.reload();
    } catch (error) {
      messageApi.error(error?.message || "Failed to accept invite");
    }
  };

  const handleRejectInvite = async (inviteId) => {
    try {
      await rejectWorkspaceInvite(inviteId);
      messageApi.success("Workspace invite rejected");
      setPendingInvitesForUser(prev => prev.filter(inv => inv.id !== inviteId));
    } catch (error) {
      messageApi.error(error?.message || "Failed to reject invite");
    }
  };

  const openMemberPermissions = (member) => {
    if (!member) return;
    const effective = resolveMemberPermissions(member);
    setPermissionsDraft(effective);
    setPermissionsTarget(member);
    setPermissionsModalOpen(true);
  };

  const handlePermissionToggle = (key, value) => {
    setPermissionsDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleClosePermissionsModal = () => {
    setPermissionsModalOpen(false);
    setPermissionsTarget(null);
    setPermissionsDraft({});
  };

  const handleSaveMemberPermissions = async () => {
    if (!permissionsTarget?.id) return;
    if (!canManagePermissions) {
      messageApi.error("You do not have permission to update member settings.");
      return;
    }
    if (permissionsTarget.role === "owner") {
      messageApi.error("Owner permissions are locked.");
      return;
    }
    const updates = {};
    if (canManagePermissions) {
      const sanitized = {};
      permissionCatalog.forEach((item) => {
        sanitized[item.key] = Boolean(permissionsDraft[item.key]);
      });
      updates.permissions = sanitized;
    }
    if (Object.keys(updates).length === 0) {
      messageApi.info("No changes to save.");
      return;
    }
    setPermissionsSaving(true);
    try {
      await updateWorkspaceMember(activeWorkspaceId, permissionsTarget.id, updates);
      messageApi.success("Member settings updated");
      handleClosePermissionsModal();
      fetchWorkspaceDetails(activeWorkspaceId);
    } catch (error) {
      const errorData = error?.response ? await error.response.json() : { message: error?.message || "Failed to update member settings" };
      showWorkspaceError(messageApi, errorData);
    } finally {
      setPermissionsSaving(false);
    }
  };

  const refreshModelStats = () => {
    setModelStatsState(getModelStats());
  };

  const updateBrandDraft = (partial) => {
    setBrandDraft((prev) => ({ ...prev, ...partial }));
  };

  const handleSaveBrand = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    onBrandChange?.(brandDraft);
    message.success("Brand library saved");
    onKnowledgeExtrasChange?.();
  };

  const handleSaveKnowledge = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    // Knowledge data is already in state via onKnowledgeChange
    message.success("Knowledge folders saved");
    onKnowledgeExtrasChange?.();
  };

  const handleSaveTags = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    // Tags data is already in state via onTagsChange and onPostTypeTagsChange
    message.success("Tags saved");
    onKnowledgeExtrasChange?.();
  };

  const handleSaveAnalysis = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    onAnalysisKnowledgeChange?.({ notes: analysisNotes, docs: analysisDocs });
    message.success("Analysis knowledge saved");
    onKnowledgeExtrasChange?.();
  };

  const handleSaveAdCopy = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    onAdCopyKnowledgeChange?.(adCopy);
    message.success("Ad copy knowledge saved");
    onKnowledgeExtrasChange?.();
  };

  useEffect(() => {
    form.setFieldsValue({
      openai: "",
      gemini: "",
      claude: "",
      openrouter: "",
      huggingface: "",
      tribe_server_url: "",
      tribe_api_key: "",
    });
  }, [form, apiKeyStatus]);

  useEffect(() => {
    const refreshStats = async () => {
      const localBytes = 0;

      let imageBytes = 0;
      let imageCount = 0;
      let brandBytes = 0;
      let brandCount = 0;
      let analysisBytes = 0;
      let analysisCount = 0;
      let adCopyBytes = 0;
      let adCopyCount = 0;
      let blogBytes = 0;
      let blogCount = 0;
      try {
        const stats = await getKnowledgeImageStats();
        imageBytes = stats.bytes;
        imageCount = stats.count;
      } catch (error) {
        // ignore
      }

      try {
        const stats = await getBrandAssetStats();
        brandBytes = stats.bytes;
        brandCount = stats.count;
      } catch (error) {
        // ignore
      }

      try {
        const stats = await getAnalysisDocStats();
        analysisBytes = stats.bytes;
        analysisCount = stats.count;
      } catch (error) {
        // ignore
      }
      try {
        const stats = await getAdCopyDocStats();
        adCopyBytes = stats.bytes;
        adCopyCount = stats.count;
      } catch (error) {
        // ignore
      }
      try {
        const stats = await getBlogDocStats();
        blogBytes = stats.bytes;
        blogCount = stats.count;
      } catch (error) {
        // ignore
      }

      let quotaBytes = null;
      let usageBytes = null;
      if (navigator.storage?.estimate) {
        try {
          const estimate = await navigator.storage.estimate();
          quotaBytes = estimate.quota ?? null;
          usageBytes = estimate.usage ?? null;
        } catch (error) {
          // ignore
        }
      }

      setStorageStats({
        localBytes,
        imageBytes,
        imageCount,
        brandBytes,
        brandCount,
        analysisBytes,
        analysisCount,
        adCopyBytes,
        adCopyCount,
        blogBytes,
        blogCount,
        quotaBytes,
        usageBytes,
      });
    };

    refreshStats();
  }, [
    knowledgeDb,
    tags,
    brandStored,
    brandDraft.assets,
    analysisDocs,
    adCopy,
    blogGuide,
  ]);

  useEffect(() => {
    refreshModelStats();
  }, []);

  useEffect(() => {
    onAnalysisKnowledgeChange?.({ notes: analysisNotes, docs: analysisDocs });
    onKnowledgeExtrasChange?.();
  }, [analysisNotes, analysisDocs]);

  useEffect(() => {
    onAdCopyKnowledgeChange?.(adCopy);
    onKnowledgeExtrasChange?.();
  }, [adCopy]);

  useEffect(() => {
    onBlogKnowledgeChange?.(blogGuide);
    onKnowledgeExtrasChange?.();
  }, [blogGuide]);

  useEffect(() => {
    onBlogSeoChange?.(blogSeoDraft);
    onKnowledgeExtrasChange?.();
  }, [blogSeoDraft]);

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const aggregateFastest = (bucket) => {
    const entries = Object.entries(bucket || {});
    if (entries.length === 0) return null;
    let best = null;
    for (const [model, stats] of entries) {
      if (!stats?.count || !stats?.totalMs) continue;
      const avg = stats.totalMs / stats.count;
      if (!best || avg < best.avgMs) {
        best = { model, avgMs: avg, count: stats.count };
      }
    }
    return best;
  };

  const aggregateErrors = () => {
    const categories = ["analysis", "variants", "images", "videos"];
    const totals = {};
    for (const category of categories) {
      const bucket = modelStatsState?.[category] || {};
      for (const [model, stats] of Object.entries(bucket)) {
        totals[model] = (totals[model] || 0) + (stats?.errors || 0);
      }
    }
    const entries = Object.entries(totals);
    if (entries.length === 0) return null;
    let worst = null;
    for (const [model, count] of entries) {
      if (!worst || count > worst.count) {
        worst = { model, count };
      }
    }
    return worst;
  };

  const aggregatePreferences = () => {
    const likes = {};
    const dislikes = {};
    activityLibrary.forEach((item) => {
      const model =
        item.imageModel ||
        item.generationModel ||
        item.analysisModel ||
        "unknown";
      if (item.sentiment === "like") {
        likes[model] = (likes[model] || 0) + 1;
      } else if (item.sentiment === "dislike") {
        dislikes[model] = (dislikes[model] || 0) + 1;
      }
    });

    const bestLike = Object.entries(likes).reduce(
      (best, [model, count]) =>
        !best || count > best.count ? { model, count } : best,
      null,
    );
    const bestDislike = Object.entries(dislikes).reduce(
      (best, [model, count]) =>
        !best || count > best.count ? { model, count } : best,
      null,
    );
    return { bestLike, bestDislike };
  };

  const onFinish = async (values) => {
    setPersonalKeysSaving(true);
    try {
      const updates = normalizeApiKeyUpdates(values);
      if (!Object.keys(updates).length) {
        messageApi.info("Enter a new key to rotate it.");
        return;
      }
      const payload = await saveUserApiKeys({ data: updates, clear: [] });
      const nextStatus =
        payload?.apiKeyStatus || mergeApiKeyStatus(resolvedApiKeyStatus, updates, []);
      onApiKeysChange?.({ ...emptyApiKeys });
      onApiKeyStatusChange?.(nextStatus);
      form.setFieldsValue({ ...emptyApiKeys });
      messageApi.success("API keys saved to your account");
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to save API keys",
        error,
        "Settings > API Keys > Save personal keys",
      );
    } finally {
      setPersonalKeysSaving(false);
    }
  };

  const handleClearPersonalApiKeys = async () => {
    setPersonalKeysSaving(true);
    try {
      const payload = await saveUserApiKeys({ data: {}, clear: API_KEY_FIELDS });
      const nextStatus =
        payload?.apiKeyStatus ||
        mergeApiKeyStatus(resolvedApiKeyStatus, {}, API_KEY_FIELDS);
      onApiKeysChange?.({ ...emptyApiKeys });
      onApiKeyStatusChange?.(nextStatus);
      form.setFieldsValue({ ...emptyApiKeys });
      messageApi.success("Personal API keys cleared");
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to clear API keys",
        error,
        "Settings > API Keys > Clear personal keys",
      );
    } finally {
      setPersonalKeysSaving(false);
    }
  };

  const handleClearSinglePersonalKey = async (field) => {
    setPersonalKeysSaving(true);
    try {
      const payload = await saveUserApiKeys({ data: {}, clear: [field] });
      const nextStatus =
        payload?.apiKeyStatus ||
        mergeApiKeyStatus(resolvedApiKeyStatus, {}, [field]);
      onApiKeysChange?.({ ...emptyApiKeys });
      onApiKeyStatusChange?.(nextStatus);
      form.setFieldValue(field, "");
      messageApi.success(`${field} key cleared`);
    } catch (error) {
      showErrorWithCopy(messageApi, "Failed to clear key", error, `Settings > API Keys > Clear ${field}`);
    } finally {
      setPersonalKeysSaving(false);
    }
  };

  const handleClearSingleWorkspaceKey = async (field) => {
    if (!activeWorkspaceId) return;
    setWorkspaceKeysSaving(true);
    try {
      const payload = await saveWorkspaceApiKeys(activeWorkspaceId, {}, [field]);
      const nextStatus =
        payload?.apiKeyStatus ||
        mergeApiKeyStatus(resolvedWorkspaceApiKeyStatus, {}, [field]);
      onWorkspaceApiKeyStatusChange?.(nextStatus);
      setWorkspaceKeysDraft((prev) => ({ ...prev, [field]: "" }));
      messageApi.success(`Workspace ${field} key cleared`);
    } catch (error) {
      showErrorWithCopy(messageApi, "Failed to clear workspace key", error, `Settings > API Keys > Clear workspace ${field}`);
    } finally {
      setWorkspaceKeysSaving(false);
    }
  };

  const handleAddAiUrl = () => {
    const value = aiUrlInput.trim();
    if (!value) return;
    if (!/^https?:\/\//i.test(value)) {
      message.error("URLs must start with http:// or https://");
      return;
    }
    const exists = aiProfileState.referenceUrls.some(
      (url) => url.toLowerCase() === value.toLowerCase(),
    );
    if (exists) {
      message.info("URL already added.");
      return;
    }
    setAiProfileState((prev) => ({
      ...prev,
      referenceUrls: [...prev.referenceUrls, value],
    }));
    setAiUrlInput("");
  };

  const handleRemoveAiUrl = (url) => {
    setAiProfileState((prev) => ({
      ...prev,
      referenceUrls: prev.referenceUrls.filter((item) => item !== url),
    }));
  };

  const handleSaveAiProfile = () => {
    try {
      if (!canEditKnowledge) {
        messageApi.error("This workspace is read-only for your role.");
        return;
      }
      onAiProfileChange?.(aiProfileState);
      message.success("AI profile saved");
      onKnowledgeExtrasChange?.();
    } catch (error) {
      message.error("Failed to save AI profile");
    }
  };

  const handleExportKnowledge = () => {
    try {
      const bundle = buildKnowledgeBundle({
        knowledgeDb,
        tags,
        postTypeTags,
        postExamples,
        brandLibrary: brandStored,
        aiProfile: aiProfileState,
        analysisKnowledge: { notes: analysisNotes, docs: analysisDocs },
        adCopyKnowledge: adCopy,
        blogKnowledge: blogGuide,
        blogSeo: blogSeoDraft,
        activityPreferences,
      });
      const blob = new Blob([JSON.stringify(bundle, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `knowledge-export-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      message.success("Knowledge export downloaded");
    } catch (error) {
      message.error("Failed to export knowledge");
    }
  };

  const handleImportKnowledge = async (file) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const normalized = normalizeKnowledgeBundle(parsed);
      onKnowledgeChange?.(normalized.knowledgeDb);
      onTagsChange?.(normalized.tags);
      onPostTypeTagsChange?.(normalized.postTypeTags);
      onPostExamplesChange?.(normalized.postExamples);
      onBrandChange?.(normalized.brandLibrary);
      setAiProfileState(normalized.aiProfile);
      setAnalysisNotes(normalized.analysisKnowledge?.notes || "");
      setAnalysisDocs(normalized.analysisKnowledge?.docs || []);
      setAdCopy(
        normalized.adCopyKnowledge || {
          notes: "",
          avoid: "",
          docs: [],
          headlines: Array.from({ length: 5 }, () => ""),
          primaryTexts: Array.from({ length: 5 }, () => ""),
        },
      );
      setBlogGuide(
        normalized.blogKnowledge || {
          notes: "",
          goals: "",
          audience: "",
          mustInclude: "",
          avoid: "",
          cta: "",
          docs: [],
        },
      );
      setBlogSeoDraft(
        normalized.blogSeo || {
          metaTitle: "",
          metaDescription: "",
          keywords: [],
          slug: "",
          canonicalUrl: "",
          schemaMarkup: "",
        },
      );
      onAiProfileChange?.(normalized.aiProfile || {});
      onAnalysisKnowledgeChange?.(normalized.analysisKnowledge || {});
      onAdCopyKnowledgeChange?.(normalized.adCopyKnowledge || {});
      onBlogKnowledgeChange?.(normalized.blogKnowledge || {});
      onBlogSeoChange?.(normalized.blogSeo || {});
      onActivityPreferencesChange?.(
        Array.isArray(normalized.activityPreferences)
          ? normalized.activityPreferences
          : [],
      );
      onKnowledgeExtrasChange?.();
      message.success("Knowledge imported");
    } catch (error) {
      message.error("Invalid knowledge export");
    } finally {
      setImporting(false);
    }
  };

  const handleAddFolder = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const trimmedName = folderName.trim();
    if (!trimmedName) {
      message.error("Folder name is required");
      return;
    }

    const exists = knowledgeDb.some(
      (item) => item.name.toLowerCase() === trimmedName.toLowerCase(),
    );
    if (exists) {
      message.error("A folder with that name already exists");
      return;
    }

    const next = [
      {
        id: `kb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: trimmedName,
        notes: folderNotes.trim(),
        images: folderImages,
        createdAt: new Date().toISOString(),
      },
      ...knowledgeDb,
    ];
    onKnowledgeChange?.(next);
    setFolderName("");
    setFolderNotes("");
    setFolderImages([]);
    message.success("Folder added");
  };

  const handleDeleteFolder = async (id) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const folder = knowledgeDb.find((item) => item.id === id);
    if (folder?.images?.length) {
      for (const img of folder.images) {
        try {
          await deleteKnowledgeImage(img.id);
        } catch (error) {
          // ignore
        }
      }
    }
    const next = knowledgeDb.filter((item) => item.id !== id);
    onKnowledgeChange?.(next);
    message.success("Folder deleted");
  };

  const runHealthCheck = async () => {
    setHealthLoading(true);
    const results = [];
    const addResult = (label, ok, detail, category) => {
      results.push({
        label,
        ok: Boolean(ok),
        detail: detail || "",
        category: category || "General",
      });
    };

    const testEndpoint = async (label, url, options = {}) => {
      const category = options.category || "API";
      const method = options.method || "GET";
      try {
        const start = Date.now();
        const fetchOptions = {
          method,
          credentials: "include",
          ...(options.fetchOptions || {}),
        };
        if (options.body && method !== "GET") {
          fetchOptions.body = JSON.stringify(options.body);
          fetchOptions.headers = {
            "Content-Type": "application/json",
            ...(fetchOptions.headers || {}),
          };
        }
        const response = await fetch(url, fetchOptions);
        const latency = Date.now() - start;
        let detail = "";
        if (response.ok) {
          let body = null;
          try {
            body = await response.json();
          } catch (e) {
            // non-JSON response is OK
          }
          const extra = options.checkBody?.(body) || "";
          detail = `${response.status} OK (${latency}ms)${extra ? ` — ${extra}` : ""}`;
          addResult(label, true, detail, category);
        } else {
          let errorMsg = `HTTP ${response.status}`;
          try {
            const body = await response.json();
            if (body?.error) errorMsg += `: ${body.error}`;
          } catch (e) {
            // ignore
          }
          addResult(label, false, `${errorMsg} (${latency}ms)`, category);
        }
      } catch (error) {
        addResult(label, false, `Request failed: ${error.message}`, category);
      }
    };

    // --- API Keys (local check) ---
    addResult(
      "OpenAI API key",
      Boolean(resolvedApiKeyStatus?.openai),
      resolvedApiKeyStatus?.openai ? "Set" : "Missing",
      "Config",
    );
    addResult(
      "Gemini API key",
      Boolean(resolvedApiKeyStatus?.gemini),
      resolvedApiKeyStatus?.gemini ? "Set" : "Missing",
      "Config",
    );
    addResult(
      "Claude API key",
      Boolean(resolvedApiKeyStatus?.claude),
      resolvedApiKeyStatus?.claude ? "Set" : "Missing",
      "Config",
    );
    addResult(
      "HuggingFace access token",
      Boolean(resolvedApiKeyStatus?.huggingface),
      resolvedApiKeyStatus?.huggingface ? "Set" : "Missing",
      "Config",
    );
    addResult(
      "Tribe Server URL",
      Boolean(resolvedApiKeyStatus?.tribe_server_url || resolvedWorkspaceApiKeyStatus?.tribe_server_url),
      resolvedApiKeyStatus?.tribe_server_url || resolvedWorkspaceApiKeyStatus?.tribe_server_url
        ? "Configured"
        : "Missing — set in personal or workspace keys for brain scan analysis",
      "Config",
    );
    addResult(
      "Tribe Server API Key",
      true,
      resolvedApiKeyStatus?.tribe_api_key || resolvedWorkspaceApiKeyStatus?.tribe_api_key
        ? "Set"
        : "Not set (optional)",
      "Config",
    );

    // --- Auth endpoints ---
    await testEndpoint("GET /api/auth/session", "/api/auth/session", {
      category: "Auth",
      checkBody: (body) =>
        body?.user?.id ? `User: ${body.user.email || body.user.id.slice(0, 8)}` : "No session",
    });

    // --- Bootstrap ---
    await testEndpoint("GET /api/bootstrap", "/api/bootstrap", {
      category: "Auth",
      checkBody: (body) => {
        const wCount = body?.workspaces?.length ?? 0;
        return `${wCount} workspace(s), tier: ${body?.profile?.subscription_tier || "unknown"}`;
      },
    });

    // --- Profile ---
    await testEndpoint("GET /api/profile", "/api/profile", {
      category: "Data",
      checkBody: (body) =>
        body?.profile?.email ? `Profile: ${body.profile.email}` : "No profile",
    });

    // --- User Settings ---
    await testEndpoint("GET /api/user-settings", "/api/user-settings", {
      category: "Data",
      checkBody: (body) =>
        body?.data ? "Settings loaded" : "Empty settings",
    });

    // --- Knowledge Bundle ---
    if (activeWorkspaceId) {
      await testEndpoint(
        "GET /api/knowledge/bundle",
        `/api/knowledge/bundle?workspace_id=${encodeURIComponent(activeWorkspaceId)}`,
        {
          category: "Data",
          checkBody: (body) => {
            if (!body?.bundle) return "No bundle found";
            const updatedAt = body.bundle.updated_at;
            return updatedAt
              ? `Last saved: ${new Date(updatedAt).toLocaleString()}`
              : "Bundle exists";
          },
        },
      );
    } else {
      addResult(
        "GET /api/knowledge/bundle",
        false,
        "No active workspace selected",
        "Data",
      );
    }

    // --- Activity Preferences ---
    await testEndpoint(
      "GET /api/activity-preferences",
      "/api/activity-preferences",
      {
        category: "Data",
        checkBody: (body) => {
          const count = Array.isArray(body?.data) ? body.data.length : 0;
          return `${count} preference(s)`;
        },
      },
    );

    // --- Workspaces ---
    await testEndpoint("GET /api/workspaces/list", "/api/workspaces/list", {
      category: "Workspaces",
      checkBody: (body) => {
        const count = Array.isArray(body?.workspaces) ? body.workspaces.length : 0;
        return `${count} workspace(s)`;
      },
    });

    if (activeWorkspaceId) {
      await testEndpoint(
        "GET /api/workspaces/details",
        `/api/workspaces/details?workspace_id=${encodeURIComponent(activeWorkspaceId)}`,
        {
          category: "Workspaces",
          checkBody: (body) =>
            body?.workspace?.name ? `Workspace: ${body.workspace.name}` : "Details loaded",
        },
      );

      await testEndpoint(
        "GET /api/workspaces/api-keys",
        `/api/workspaces/api-keys?workspace_id=${encodeURIComponent(activeWorkspaceId)}`,
        {
          category: "Workspaces",
          checkBody: (body) => {
            if (body?.canManage === false) return "Hidden for your role";
            const status = body?.status || {};
            const keys = Object.keys(status).filter((k) => status[k]);
            return keys.length ? `Keys set: ${keys.join(", ")}` : "No shared keys configured";
          },
        },
      );
    }

    // --- Assets / Storage ---
    try {
      await getKnowledgeImageStats();
      addResult("Supabase storage (knowledge images)", true, "Reachable", "Storage");
    } catch (error) {
      addResult("Supabase storage (knowledge images)", false, error.message || "Not reachable", "Storage");
    }

    try {
      await getBrandAssetStats();
      addResult("Supabase storage (brand assets)", true, "Reachable", "Storage");
    } catch (error) {
      addResult("Supabase storage (brand assets)", false, error.message || "Not reachable", "Storage");
    }

    try {
      await getAnalysisDocStats();
      addResult("Supabase storage (analysis docs)", true, "Reachable", "Storage");
    } catch (error) {
      addResult("Supabase storage (analysis docs)", false, error.message || "Not reachable", "Storage");
    }

    try {
      await getAdCopyDocStats();
      addResult("Supabase storage (ad copy docs)", true, "Reachable", "Storage");
    } catch (error) {
      addResult("Supabase storage (ad copy docs)", false, error.message || "Not reachable", "Storage");
    }

    try {
      await getBlogDocStats();
      addResult("Supabase storage (blog docs)", true, "Reachable", "Storage");
    } catch (error) {
      addResult("Supabase storage (blog docs)", false, error.message || "Not reachable", "Storage");
    }

    // --- Signed URL round-trip ---
    if (activeWorkspaceId) {
      const healthId = `health-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 6)}`;
      await testEndpoint(
        "POST /api/assets/signed-upload (round-trip)",
        "/api/assets/signed-upload",
        {
          category: "Storage",
          method: "POST",
          body: {
            workspace_id: activeWorkspaceId,
            category: "__health_check__",
            id: healthId,
            mimeType: "text/plain",
            name: "health-check.txt",
          },
          checkBody: (body) =>
            body?.signedUrl ? "Signed upload URL generated" : "No signed URL",
        },
      );
    } else {
      addResult(
        "POST /api/assets/signed-upload (round-trip)",
        false,
        "No active workspace selected",
        "Storage",
      );
    }

    // --- External services ---
    try {
      const response = await fetch("/api/meta/health", {
        credentials: "include",
      });
      if (!response.ok) {
        addResult("Meta server health", false, `HTTP ${response.status}`, "Services");
      } else {
        const data = await response.json();
        addResult(
          "Meta server health",
          Boolean(data?.ok),
          data?.ok ? "Connected" : data?.error || "Not configured",
          "Services",
        );
      }
    } catch (error) {
      addResult("Meta server health", false, "Request failed", "Services");
    }

    // --- Summary ---
    const passed = results.filter((r) => r.ok).length;
    const failed = results.filter((r) => !r.ok).length;
    addResult(
      "Summary",
      failed === 0,
      `${passed} passed, ${failed} failed out of ${results.length - 1} checks`,
      "Summary",
    );

    setHealthReport(results);
    setHealthLoading(false);
  };

  const handleAddFolderImage = async (file) => {
    if (!requireWorkspace("Adding images")) return;
    try {
      const imageId = `img-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const stored = await saveKnowledgeImage({
        id: imageId,
        name: file.name,
        blob: file,
        mimeType: file.type,
      });
      setFolderImages((prev) => [
        ...prev,
        {
          id: imageId,
          name: file.name,
          mimeType: file.type,
          url: stored?.url || "",
          path: stored?.path || "",
          signedAt: stored?.signedAt || "",
        },
      ]);
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to add image",
        error,
        "Settings > Knowledge > Add folder image",
      );
    }
  };

  const handleRemoveFolderImage = async (imageId) => {
    const image = folderImages.find((img) => img.id === imageId);
    try {
      await deleteKnowledgeImage(imageId);
    } catch (error) {
      // ignore delete errors
    }
    setFolderImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handleFolderImageName = (imageId, value) => {
    setFolderImages((prev) =>
      prev.map((img) => (img.id === imageId ? { ...img, name: value } : img)),
    );
  };

  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setEditName(folder.name);
    setEditNotes(folder.notes || "");
    setEditImages(folder.images ? [...folder.images] : []);
    setEditAddedIds([]);
    setEditRemovedIds([]);
  };

  const closeEditFolder = async () => {
    if (editAddedIds.length > 0) {
      for (const id of editAddedIds) {
        const image = editImages.find((img) => img.id === id);
        try {
          await deleteKnowledgeImage(id);
        } catch (error) {
          // ignore
        }
      }
    }
    setEditingFolder(null);
    setEditName("");
    setEditNotes("");
    setEditImages([]);
    setEditAddedIds([]);
    setEditRemovedIds([]);
  };

  const handleSaveEdit = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    if (!editName.trim()) {
      message.error("Folder name is required");
      return;
    }
    const next = knowledgeDb.map((item) =>
      item.id === editingFolder.id
        ? {
            ...item,
            name: editName.trim(),
            notes: editNotes.trim(),
            images: editImages,
          }
        : item,
    );
    onKnowledgeChange?.(next);
    if (editRemovedIds.length > 0) {
      for (const id of editRemovedIds) {
        const image = editingFolder?.images?.find((img) => img.id === id);
        if (image?.url) {
          URL.revokeObjectURL(image.url);
        }
        deleteKnowledgeImage(id).catch(() => {});
      }
    }
    setEditingFolder(null);
    setEditName("");
    setEditNotes("");
    setEditImages([]);
    setEditAddedIds([]);
    setEditRemovedIds([]);
    message.success("Folder updated");
  };

  const handleAddEditImage = async (file) => {
    if (!requireWorkspace("Adding images")) return;
    try {
      const imageId = `img-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const stored = await saveKnowledgeImage({
        id: imageId,
        name: file.name,
        blob: file,
        mimeType: file.type,
      });
      setEditImages((prev) => [
        ...prev,
        {
          id: imageId,
          name: file.name,
          mimeType: file.type,
          url: stored?.url || "",
          path: stored?.path || "",
          signedAt: stored?.signedAt || "",
        },
      ]);
      setEditAddedIds((prev) => [...prev, imageId]);
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to add image",
        error,
        "Settings > Knowledge > Edit folder image",
      );
    }
  };

  const handleRemoveEditImage = async (imageId) => {
    const image = editImages.find((img) => img.id === imageId);
    const isNew = editAddedIds.includes(imageId);
    if (isNew) {
      try {
        await deleteKnowledgeImage(imageId);
      } catch (error) {
        // ignore delete errors
      }
      setEditAddedIds((prev) => prev.filter((id) => id !== imageId));
    } else {
      setEditRemovedIds((prev) => [...prev, imageId]);
    }
    setEditImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handleEditImageName = (imageId, value) => {
    setEditImages((prev) =>
      prev.map((img) => (img.id === imageId ? { ...img, name: value } : img)),
    );
  };

  const resolvePostExampleMediaType = (item) => {
    if (!item) return "image";
    if (item.mediaType) return item.mediaType;
    const mime = String(item.type || item.mimeType || "");
    return mime.startsWith("video/") ? "video" : "image";
  };

  const analyzePostExampleMedia = async (file) => {
    const mediaType = resolvePostExampleMediaType(file);
    const aiService = new AIService({
      ...apiKeys,
      aiProfile,
      analysisKnowledge,
      adCopyKnowledge,
      tagProfile: tags,
      workspaceId: activeWorkspaceId,
    });
    const analysis = await aiService.analyzeCreative(
      file,
      mediaType === "video",
      null,
      undefined,
      { model: DEFAULT_ANALYSIS_MODEL },
    );
    const context =
      mediaType === "video"
        ? analysis?.segments
          ? analysis
          : analysis?.combined_context || analysis
        : analysis;
    return { context, mediaType };
  };

  const handleAddPostExampleImage = async (file) => {
    if (!requireWorkspace("Adding post examples")) return;
    const statusKey = `post-example-${Date.now()}`;
    try {
      const mediaType = resolvePostExampleMediaType(file);
      messageApi.loading({
        content: `Analyzing ${mediaType === "video" ? "video" : "image"}...`,
        key: statusKey,
        duration: 0,
      });
      const { context } = await analyzePostExampleMedia(file);
      const imageId = `post-example-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      let stored = null;
      if (mediaType === "image") {
        stored = await savePostExampleImage({
          id: imageId,
          name: file.name,
          blob: file,
          mimeType: file.type,
        });
      }
      setPostExampleImages((prev) => [
        ...prev,
        {
          id: imageId,
          name: file.name,
          mimeType: file.type || "",
          mediaType,
          url: stored?.url || "",
          path: stored?.path || "",
          signedAt: stored?.signedAt || "",
          context,
        },
      ]);
      messageApi.success({
        content:
          mediaType === "video"
            ? "Video context added"
            : "Post example added",
        key: statusKey,
      });
    } catch (error) {
      messageApi.error({
        content: "Failed to add post example",
        key: statusKey,
      });
      showErrorWithCopy(
        messageApi,
        "Failed to add post example",
        error,
        "Settings > Knowledge > Post Examples > Add image",
      );
    }
  };

  const handleRemovePostExampleImage = async (imageId) => {
    const target = postExampleImages.find((img) => img.id === imageId);
    try {
      if (target?.path) {
        await deletePostExampleImage(imageId);
      }
    } catch (error) {
      // ignore
    }
    setPostExampleImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handlePostExampleImageName = (imageId, value) => {
    setPostExampleImages((prev) =>
      prev.map((img) => (img.id === imageId ? { ...img, name: value } : img)),
    );
  };

  const openEditPostExampleFolder = (folder) => {
    setEditingPostExampleFolder(folder);
    setEditPostExampleName(folder.name || "");
    setEditPostExampleNotes(folder.notes || "");
    setEditPostExampleImages(folder.images ? [...folder.images] : []);
    setEditPostExampleAddedIds([]);
    setEditPostExampleRemovedIds([]);
    setEditPostExampleTypeId(folder.postTypeId || "");
    setEditPostExampleTags(normalizePostExampleTags(folder.tags));
  };

  const closeEditPostExampleFolder = async () => {
    if (editPostExampleAddedIds.length > 0) {
      for (const id of editPostExampleAddedIds) {
        try {
          await deletePostExampleImage(id);
        } catch (error) {
          // ignore
        }
      }
    }
    setEditingPostExampleFolder(null);
    setEditPostExampleName("");
    setEditPostExampleNotes("");
    setEditPostExampleImages([]);
    setEditPostExampleAddedIds([]);
    setEditPostExampleRemovedIds([]);
    setEditPostExampleTypeId("");
    setEditPostExampleTags([]);
  };

  const handleSavePostExampleEdit = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    if (!editPostExampleName.trim()) {
      message.error("Folder name is required");
      return;
    }
    const next = postExamples.map((item) =>
      item.id === editingPostExampleFolder.id
        ? {
            ...item,
            name: editPostExampleName.trim(),
            notes: editPostExampleNotes.trim(),
            images: editPostExampleImages,
            postTypeId: editPostExampleTypeId || "",
            tags: normalizePostExampleTags(editPostExampleTags),
          }
        : item,
    );
    onPostExamplesChange?.(next);
    if (editPostExampleRemovedIds.length > 0) {
      for (const id of editPostExampleRemovedIds) {
        deletePostExampleImage(id).catch(() => {});
      }
    }
    setEditingPostExampleFolder(null);
    setEditPostExampleName("");
    setEditPostExampleNotes("");
    setEditPostExampleImages([]);
    setEditPostExampleAddedIds([]);
    setEditPostExampleRemovedIds([]);
    setEditPostExampleTypeId("");
    setEditPostExampleTags([]);
    message.success("Post example updated");
  };

  const handleAddPostExampleEditImage = async (file) => {
    if (!requireWorkspace("Adding post examples")) return;
    const statusKey = `post-example-edit-${Date.now()}`;
    try {
      const mediaType = resolvePostExampleMediaType(file);
      messageApi.loading({
        content: `Analyzing ${mediaType === "video" ? "video" : "image"}...`,
        key: statusKey,
        duration: 0,
      });
      const { context } = await analyzePostExampleMedia(file);
      const imageId = `post-example-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      let stored = null;
      if (mediaType === "image") {
        stored = await savePostExampleImage({
          id: imageId,
          name: file.name,
          blob: file,
          mimeType: file.type,
        });
      }
      setEditPostExampleImages((prev) => [
        ...prev,
        {
          id: imageId,
          name: file.name,
          mimeType: file.type || "",
          mediaType,
          url: stored?.url || "",
          path: stored?.path || "",
          signedAt: stored?.signedAt || "",
          context,
        },
      ]);
      if (mediaType === "image") {
        setEditPostExampleAddedIds((prev) => [...prev, imageId]);
      }
      messageApi.success({
        content:
          mediaType === "video"
            ? "Video context added"
            : "Post example added",
        key: statusKey,
      });
    } catch (error) {
      messageApi.error({
        content: "Failed to add post example",
        key: statusKey,
      });
      showErrorWithCopy(
        messageApi,
        "Failed to add post example",
        error,
        "Settings > Knowledge > Post Examples > Edit image",
      );
    }
  };

  const handleRemovePostExampleEditImage = async (imageId) => {
    const target = editPostExampleImages.find((img) => img.id === imageId);
    const isStored = Boolean(target?.path);
    const isNew = editPostExampleAddedIds.includes(imageId);
    if (isStored && isNew) {
      try {
        await deletePostExampleImage(imageId);
      } catch (error) {
        // ignore
      }
      setEditPostExampleAddedIds((prev) => prev.filter((id) => id !== imageId));
    } else if (isStored) {
      setEditPostExampleRemovedIds((prev) => [...prev, imageId]);
    }
    setEditPostExampleImages((prev) =>
      prev.filter((img) => img.id !== imageId),
    );
  };

  const handleEditPostExampleImageName = (imageId, value) => {
    setEditPostExampleImages((prev) =>
      prev.map((img) => (img.id === imageId ? { ...img, name: value } : img)),
    );
  };

  const isPostExampleVideo = (item) =>
    resolvePostExampleMediaType(item) === "video";

  const renderPostExamplePreview = (item, { height = 48 } = {}) => {
    if (isPostExampleVideo(item)) {
      return (
        <div className="post-example-thumb" style={{ height }}>
          <PlayCircleOutlined />
          <span>Video</span>
        </div>
      );
    }
    return (
      <img
        src={item.url || item.dataUrl}
        alt={item.name}
        style={{
          width: "100%",
          height,
          objectFit: "cover",
          borderRadius: 4,
        }}
      />
    );
  };

  const handleAddPostExampleFolder = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const trimmedName = postExampleName.trim();
    if (!trimmedName) {
      message.error("Folder name is required");
      return;
    }
    const next = [
      {
        id: `post-folder-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        name: trimmedName,
        notes: postExampleNotes.trim(),
        images: postExampleImages,
        postTypeId: postExampleTypeId || "",
        tags: normalizePostExampleTags(postExampleTags),
        createdAt: new Date().toISOString(),
      },
      ...postExamples,
    ];
    onPostExamplesChange?.(next);
    setPostExampleName("");
    setPostExampleNotes("");
    setPostExampleImages([]);
    setPostExampleTypeId("");
    setPostExampleTags([]);
    message.success("Post example folder added");
  };

  const handleDeletePostExampleFolder = async (id) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const folder = postExamples.find((item) => item.id === id);
    if (folder?.images?.length) {
      for (const img of folder.images) {
        try {
          if (img?.path) {
            await deletePostExampleImage(img.id);
          }
        } catch (error) {
          // ignore
        }
      }
    }
    const next = postExamples.filter((item) => item.id !== id);
    onPostExamplesChange?.(next);
    message.success("Post example folder deleted");
  };

  const handleAddTag = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const name = tagName.trim();
    if (!name) {
      message.error("Tag name is required");
      return;
    }
    const exists = tags.some(
      (tag) => tag.name.toLowerCase() === name.toLowerCase(),
    );
    if (exists) {
      message.error("Tag already exists");
      return;
    }
    const next = [
      {
        id: `tag-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name,
        description: tagDescription.trim(),
        starred: false,
      },
      ...tags,
    ];
    onTagsChange?.(next);
    setTagName("");
    setTagDescription("");
    message.success("Tag added");
  };

  const handleDeleteTag = (id) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const next = tags.filter((tag) => tag.id !== id);
    onTagsChange?.(next);
    message.success("Tag removed");
  };

  const openEditTag = (tag) => {
    setEditingTag(tag);
    setEditTagName(tag.name || "");
    setEditTagDescription(tag.description || "");
  };

  const closeEditTag = () => {
    setEditingTag(null);
    setEditTagName("");
    setEditTagDescription("");
  };

  const handleSaveTagEdit = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const name = editTagName.trim();
    if (!name) {
      message.error("Tag name is required");
      return;
    }
    const conflict = tags.some(
      (tag) =>
        tag.id !== editingTag?.id &&
        tag.name.toLowerCase() === name.toLowerCase(),
    );
    if (conflict) {
      message.error("Tag already exists");
      return;
    }
    const next = tags.map((tag) =>
      tag.id === editingTag.id
        ? { ...tag, name, description: editTagDescription.trim() }
        : tag,
    );
    onTagsChange?.(next);
    closeEditTag();
    message.success("Tag updated");
  };

  const handleToggleTagStar = (tagId) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const starCount = tags.filter((tag) => Boolean(tag.starred)).length;
    const target = tags.find((tag) => tag.id === tagId);
    if (!target) return;
    if (!target.starred && starCount >= 3) {
      message.error("You can star up to 3 tags.");
      return;
    }
    const next = tags.map((tag) =>
      tag.id === tagId ? { ...tag, starred: !tag.starred } : tag,
    );
    onTagsChange?.(next);
  };

  const handleTogglePostTypeTagStar = (tagId) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const starCount = postTypeTags.filter((tag) => Boolean(tag.starred)).length;
    const target = postTypeTags.find((tag) => tag.id === tagId);
    if (!target) return;
    if (!target.starred && starCount >= 3) {
      message.error("You can star up to 3 post types.");
      return;
    }
    const next = postTypeTags.map((tag) =>
      tag.id === tagId ? { ...tag, starred: !tag.starred } : tag,
    );
    onPostTypeTagsChange?.(next);
  };

  const handleAddPostTypeTagDoc = async (file) => {
    if (!requireWorkspace("Adding post type references")) return;
    try {
      const docId = `ptdoc-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const stored = await savePostTypeDoc({
        id: docId,
        name: file.name,
        blob: file,
        mimeType: file.type,
      });
      setPostTypeTagDocs((prev) => [
        ...prev,
        {
          id: docId,
          name: file.name,
          mimeType: file.type,
          size: file.size,
          url: stored?.url || "",
          path: stored?.path || "",
          signedAt: stored?.signedAt || "",
        },
      ]);
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to add post type reference",
        error,
        "Settings > Tags > Add post type doc",
      );
    }
  };

  const handleRemovePostTypeTagDoc = async (docId) => {
    try {
      await deletePostTypeDoc(docId);
    } catch (error) {
      // ignore
    }
    setPostTypeTagDocs((prev) => prev.filter((doc) => doc.id !== docId));
  };

  const handleAddPostTypeTagEditDoc = async (file) => {
    if (!requireWorkspace("Adding post type references")) return;
    try {
      const docId = `ptdoc-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const stored = await savePostTypeDoc({
        id: docId,
        name: file.name,
        blob: file,
        mimeType: file.type,
      });
      setEditPostTypeTagDocs((prev) => [
        ...prev,
        {
          id: docId,
          name: file.name,
          mimeType: file.type,
          size: file.size,
          url: stored?.url || "",
          path: stored?.path || "",
          signedAt: stored?.signedAt || "",
        },
      ]);
      setEditPostTypeTagAddedIds((prev) => [...prev, docId]);
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to add post type reference",
        error,
        "Settings > Tags > Edit post type doc",
      );
    }
  };

  const handleRemovePostTypeTagEditDoc = async (docId) => {
    if (editPostTypeTagAddedIds.includes(docId)) {
      try {
        await deletePostTypeDoc(docId);
      } catch (error) {
        // ignore
      }
      setEditPostTypeTagAddedIds((prev) => prev.filter((id) => id !== docId));
    } else {
      setEditPostTypeTagRemovedIds((prev) => [...prev, docId]);
    }
    setEditPostTypeTagDocs((prev) => prev.filter((doc) => doc.id !== docId));
  };

  const handleAddPostTypeTag = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const name = postTypeTagName.trim();
    if (!name) {
      message.error("Post type name is required");
      return;
    }
    const exists = postTypeTags.some(
      (tag) => tag.name.toLowerCase() === name.toLowerCase(),
    );
    if (exists) {
      message.error("Post type already exists");
      return;
    }
    const next = [
      {
        id: `post-type-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name,
        description: postTypeTagDescription.trim(),
        extraInfo: postTypeTagExtraInfo.trim(),
        format: normalizePostTypeFormat(postTypeTagFormat),
        docs: postTypeTagDocs,
        starred: false,
      },
      ...postTypeTags,
    ];
    onPostTypeTagsChange?.(next);
    setPostTypeTagName("");
    setPostTypeTagDescription("");
    setPostTypeTagExtraInfo("");
    setPostTypeTagFormat("meta_ads");
    setPostTypeTagDocs([]);
    message.success("Post type added");
  };

  const handleDeletePostTypeTag = (id) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const target = postTypeTags.find((tag) => tag.id === id);
    if (target?.docs?.length) {
      for (const doc of target.docs) {
        try {
          deletePostTypeDoc(doc.id);
        } catch (error) {
          // ignore
        }
      }
    }
    const next = postTypeTags.filter((tag) => tag.id !== id);
    onPostTypeTagsChange?.(next);
    message.success("Post type removed");
  };

  const openEditPostTypeTag = (tag) => {
    setEditingPostTypeTag(tag);
    setEditPostTypeTagName(tag.name || "");
    setEditPostTypeTagDescription(tag.description || "");
    setEditPostTypeTagExtraInfo(tag.extraInfo || "");
    setEditPostTypeTagFormat(normalizePostTypeFormat(tag.format));
    setEditPostTypeTagDocs(Array.isArray(tag.docs) ? [...tag.docs] : []);
    setEditPostTypeTagAddedIds([]);
    setEditPostTypeTagRemovedIds([]);
  };

  const closeEditPostTypeTag = () => {
    if (editPostTypeTagAddedIds.length > 0) {
      for (const id of editPostTypeTagAddedIds) {
        try {
          deletePostTypeDoc(id);
        } catch (error) {
          // ignore
        }
      }
    }
    setEditingPostTypeTag(null);
    setEditPostTypeTagName("");
    setEditPostTypeTagDescription("");
    setEditPostTypeTagExtraInfo("");
    setEditPostTypeTagFormat("meta_ads");
    setEditPostTypeTagDocs([]);
    setEditPostTypeTagAddedIds([]);
    setEditPostTypeTagRemovedIds([]);
  };

  const handleSavePostTypeTagEdit = async () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const name = editPostTypeTagName.trim();
    if (!name) {
      message.error("Post type name is required");
      return;
    }
    const conflict = postTypeTags.some(
      (tag) =>
        tag.id !== editingPostTypeTag?.id &&
        tag.name.toLowerCase() === name.toLowerCase(),
    );
    if (conflict) {
      message.error("Post type already exists");
      return;
    }
    if (editPostTypeTagRemovedIds.length > 0) {
      for (const id of editPostTypeTagRemovedIds) {
        try {
          await deletePostTypeDoc(id);
        } catch (error) {
          // ignore
        }
      }
    }
    const next = postTypeTags.map((tag) =>
      tag.id === editingPostTypeTag.id
        ? {
            ...tag,
            name,
            description: editPostTypeTagDescription.trim(),
            extraInfo: editPostTypeTagExtraInfo.trim(),
            format: normalizePostTypeFormat(editPostTypeTagFormat),
            docs: editPostTypeTagDocs,
          }
        : tag,
    );
    onPostTypeTagsChange?.(next);
    closeEditPostTypeTag();
    message.success("Post type updated");
  };

  const getFileExtension = (name) => {
    const raw = String(name || "").trim().toLowerCase();
    const dot = raw.lastIndexOf(".");
    if (dot < 0) return "";
    return raw.slice(dot);
  };

  const buildAnalysisDocKey = (name, size) =>
    `${String(name || "").trim().toLowerCase()}::${Number(size) || 0}`;

  const normalizeAnalysisFiles = (items) =>
    (Array.isArray(items) ? items : [items])
      .map((item) => item?.originFileObj || item)
      .filter(
        (file) =>
          file &&
          typeof file.name === "string" &&
          Number.isFinite(Number(file.size || 0)),
      );

  const buildAnalysisSkipSummary = (counts = {}) => {
    const details = [];
    if (counts.unsupported > 0) {
      details.push(`${counts.unsupported} unsupported format`);
    }
    if (counts.tooLarge > 0) {
      details.push(
        `${counts.tooLarge} over ${formatBytes(
          ANALYSIS_UPLOAD_LIMITS.maxFileSizeBytes,
        )}`,
      );
    }
    if (counts.duplicate > 0) {
      details.push(`${counts.duplicate} duplicate`);
    }
    if (counts.batchLimit > 0) {
      details.push(
        `${counts.batchLimit} over batch limit (${ANALYSIS_UPLOAD_LIMITS.maxFilesPerBatch})`,
      );
    }
    if (counts.totalLimit > 0) {
      details.push(
        `${counts.totalLimit} over total limit (${ANALYSIS_UPLOAD_LIMITS.maxTotalDocs})`,
      );
    }
    return details.join(", ");
  };

  const uploadAnalysisDoc = async (file) => {
    const docId = `doc-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const stored = await saveAnalysisDoc({
      id: docId,
      name: file.name,
      blob: file,
      mimeType: file.type,
    });
    let text = "";
    const lower = file.name.toLowerCase();
    if (
      file.type.startsWith("text/") ||
      lower.endsWith(".txt") ||
      lower.endsWith(".md") ||
      lower.endsWith(".json")
    ) {
      try {
        const raw = await file.text();
        text = raw.slice(0, 4000);
      } catch (error) {
        text = "";
      }
    }
    setAnalysisDocs((prev) => [
      {
        id: docId,
        name: file.name,
        mimeType: file.type,
        size: file.size,
        text,
        url: stored?.url || "",
        path: stored?.path || "",
        signedAt: stored?.signedAt || "",
      },
      ...prev,
    ]);
  };

  const handleAddAnalysisDocs = async (items) => {
    if (!requireWorkspace("Adding analysis references")) return;
    if (analysisUploadLockRef.current || analysisUploadState.active) {
      messageApi.info("Analysis upload is already running.");
      return;
    }
    analysisUploadLockRef.current = true;

    const rawFiles = normalizeAnalysisFiles(items);
    if (!rawFiles.length) {
      analysisUploadLockRef.current = false;
      return;
    }

    const skipCounts = {
      unsupported: 0,
      tooLarge: 0,
      duplicate: 0,
      batchLimit: 0,
      totalLimit: 0,
    };
    const knownKeys = new Set(
      (analysisDocs || []).map((doc) => buildAnalysisDocKey(doc?.name, doc?.size)),
    );
    const accepted = [];
    for (const file of rawFiles) {
      const ext = getFileExtension(file.name);
      if (!ANALYSIS_ALLOWED_EXTENSIONS.has(ext)) {
        skipCounts.unsupported += 1;
        continue;
      }
      if ((file.size || 0) > ANALYSIS_UPLOAD_LIMITS.maxFileSizeBytes) {
        skipCounts.tooLarge += 1;
        continue;
      }
      const key = buildAnalysisDocKey(file.name, file.size);
      if (knownKeys.has(key)) {
        skipCounts.duplicate += 1;
        continue;
      }
      knownKeys.add(key);
      accepted.push(file);
    }

    if (accepted.length > ANALYSIS_UPLOAD_LIMITS.maxFilesPerBatch) {
      skipCounts.batchLimit +=
        accepted.length - ANALYSIS_UPLOAD_LIMITS.maxFilesPerBatch;
      accepted.splice(ANALYSIS_UPLOAD_LIMITS.maxFilesPerBatch);
    }

    const remainingCapacity = Math.max(
      0,
      ANALYSIS_UPLOAD_LIMITS.maxTotalDocs - (analysisDocs?.length || 0),
    );
    if (accepted.length > remainingCapacity) {
      skipCounts.totalLimit += accepted.length - remainingCapacity;
      accepted.splice(remainingCapacity);
    }

    const skippedTotal = Object.values(skipCounts).reduce(
      (sum, count) => sum + count,
      0,
    );
    if (!accepted.length) {
      const skipDetail = buildAnalysisSkipSummary(skipCounts);
      if (skipDetail) {
        messageApi.warning(`No files uploaded (${skipDetail}).`);
      }
      analysisUploadLockRef.current = false;
      return;
    }

    setAnalysisUploadState({
      active: true,
      total: accepted.length,
      completed: 0,
      failed: 0,
      skipped: skippedTotal,
    });

    const failures = [];
    const queue = [...accepted];
    const workerCount = Math.min(
      ANALYSIS_UPLOAD_LIMITS.maxConcurrent,
      queue.length,
    );
    const workers = Array.from({ length: workerCount }, async () => {
      while (queue.length > 0) {
        const file = queue.shift();
        if (!file) break;
        try {
          await uploadAnalysisDoc(file);
        } catch (error) {
          failures.push({ file, error });
          setAnalysisUploadState((prev) => ({
            ...prev,
            completed: prev.completed + 1,
            failed: prev.failed + 1,
          }));
          continue;
        }
        setAnalysisUploadState((prev) => ({
          ...prev,
          completed: prev.completed + 1,
        }));
      }
    });

    try {
      await Promise.all(workers);
      const uploadedCount = accepted.length - failures.length;
      const completedState = {
        active: false,
        total: accepted.length,
        completed: accepted.length,
        failed: failures.length,
        skipped: skippedTotal,
      };
      setAnalysisUploadState(completedState);

      if (uploadedCount > 0) {
        messageApi.success(
          `Added ${uploadedCount} analysis reference${uploadedCount === 1 ? "" : "s"}.`,
        );
      }
      if (skippedTotal > 0) {
        const skipDetail = buildAnalysisSkipSummary(skipCounts);
        messageApi.info(
          `Skipped ${skippedTotal} file${skippedTotal === 1 ? "" : "s"}${
            skipDetail ? ` (${skipDetail})` : ""
          }.`,
        );
      }
      if (failures.length > 0) {
        if (accepted.length === 1 && failures[0]?.error) {
          showErrorWithCopy(
            messageApi,
            "Failed to add analysis reference",
            failures[0].error,
            "Settings > Knowledge > Analysis > Add reference",
          );
        } else {
          messageApi.warning(
            `${failures.length} upload${failures.length === 1 ? "" : "s"} failed.`,
          );
        }
      }
    } finally {
      analysisUploadLockRef.current = false;
      setAnalysisUploadState((prev) => ({ ...prev, active: false }));
    }
  };

  const handleRemoveAnalysisDoc = async (docId) => {
    try {
      await deleteAnalysisDoc(docId);
    } catch (error) {
      // ignore
    }
    setAnalysisDocs((prev) => prev.filter((doc) => doc.id !== docId));
  };

  const handleAddAdCopyDoc = async (file) => {
    if (!requireWorkspace("Adding ad copy references")) return;
    try {
      const docId = `adcopy-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const stored = await saveAdCopyDoc({
        id: docId,
        name: file.name,
        blob: file,
        mimeType: file.type,
      });
      let text = "";
      const lower = file.name.toLowerCase();
      if (
        file.type.startsWith("text/") ||
        lower.endsWith(".txt") ||
        lower.endsWith(".md") ||
        lower.endsWith(".json")
      ) {
        try {
          const raw = await file.text();
          text = raw.slice(0, 4000);
        } catch (error) {
          text = "";
        }
      }
      setAdCopy((prev) => ({
        ...prev,
        docs: [
          {
            id: docId,
            name: file.name,
            mimeType: file.type,
            size: file.size,
            text,
            url: stored?.url || "",
            path: stored?.path || "",
            signedAt: stored?.signedAt || "",
          },
          ...(prev.docs || []),
        ],
      }));
      message.success("Ad copy reference added");
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to add ad copy reference",
        error,
        "Settings > Knowledge > Copy > Add reference",
      );
    }
  };

  const handleRemoveAdCopyDoc = async (docId) => {
    try {
      await deleteAdCopyDoc(docId);
    } catch (error) {
      // ignore
    }
    setAdCopy((prev) => ({
      ...prev,
      docs: (prev.docs || []).filter((doc) => doc.id !== docId),
    }));
  };

  const handleAddBlogDoc = async (file) => {
    if (!requireWorkspace("Adding blog references")) return;
    try {
      const docId = `blog-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const stored = await saveBlogDoc({
        id: docId,
        name: file.name,
        blob: file,
        mimeType: file.type,
      });
      let text = "";
      const lower = file.name.toLowerCase();
      if (
        file.type.startsWith("text/") ||
        lower.endsWith(".txt") ||
        lower.endsWith(".md") ||
        lower.endsWith(".json")
      ) {
        try {
          const raw = await file.text();
          text = raw.slice(0, 4000);
        } catch (error) {
          text = "";
        }
      }
      setBlogGuide((prev) => ({
        ...prev,
        docs: [
          {
            id: docId,
            name: file.name,
            mimeType: file.type,
            size: file.size,
            text,
            url: stored?.url || "",
            path: stored?.path || "",
            signedAt: stored?.signedAt || "",
          },
          ...(prev.docs || []),
        ],
      }));
      message.success("Blog reference added");
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to add blog reference",
        error,
        "Settings > Knowledge > Blog > Add reference",
      );
    }
  };

  const handleRemoveBlogDoc = async (docId) => {
    try {
      await deleteBlogDoc(docId);
    } catch (error) {
      // ignore
    }
    setBlogGuide((prev) => ({
      ...prev,
      docs: (prev.docs || []).filter((doc) => doc.id !== docId),
    }));
  };

  const updateAdCopy = (key, index, value) => {
    setAdCopy((prev) => {
      const next = { ...prev, [key]: [...prev[key]] };
      next[key][index] = value;
      return next;
    });
  };

  const handleAddBrandAsset = async (file) => {
    if (!requireWorkspace("Adding brand assets")) return;
    try {
      const assetId = `brand-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const stored = await saveBrandAsset({
        id: assetId,
        name: file.name,
        blob: file,
        mimeType: file.type,
      });
      setBrandDraft((prev) => ({
        ...prev,
        assets: [
          {
            id: assetId,
            name: file.name,
            mimeType: file.type,
            url: stored?.url || "",
            path: stored?.path || "",
            signedAt: stored?.signedAt || "",
          },
          ...(prev.assets || []),
        ],
      }));
      message.success("Brand asset added");
    } catch (error) {
      showErrorWithCopy(
        messageApi,
        "Failed to add brand asset",
        error,
        "Settings > Library > Brand > Add asset",
      );
    }
  };

  const handleRemoveBrandAsset = async (assetId) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const asset = brandDraft.assets?.find((item) => item.id === assetId);
    try {
      await deleteBrandAsset(assetId);
    } catch (error) {
      // ignore
    }
    setBrandDraft((prev) => ({
      ...prev,
      assets: (prev.assets || []).filter((item) => item.id !== assetId),
    }));
    message.success("Brand asset deleted");
  };

  const handleBrandAssetNameChange = (assetId, value) => {
    setBrandDraft((prev) => ({
      ...prev,
      assets: (prev.assets || []).map((item) =>
        item.id === assetId ? { ...item, name: value } : item,
      ),
    }));
  };

  const splitFileName = (name) => {
    const raw = String(name || "").trim();
    const dotIndex = raw.lastIndexOf(".");
    if (dotIndex <= 0) {
      return { base: raw, ext: "" };
    }
    return {
      base: raw.slice(0, dotIndex),
      ext: raw.slice(dotIndex),
    };
  };

  const handleOpenBrandAssetRename = (asset) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const { base } = splitFileName(asset?.name || "");
    setEditingBrandAsset(asset);
    setBrandAssetBaseNameDraft(base);
  };

  const handleSaveBrandAssetRename = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    if (!editingBrandAsset) return;
    const trimmedBase = brandAssetBaseNameDraft.trim();
    if (!trimmedBase) {
      message.error("File name is required");
      return;
    }
    const { ext } = splitFileName(editingBrandAsset.name || "");
    const nextName = `${trimmedBase}${ext}`;
    handleBrandAssetNameChange(editingBrandAsset.id, nextName);
    setEditingBrandAsset(null);
    setBrandAssetBaseNameDraft("");
  };

  const handleCloseBrandAssetRename = () => {
    setEditingBrandAsset(null);
    setBrandAssetBaseNameDraft("");
  };

  const handleAddColor = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const value = newColor.trim();
    if (!value) return;
    if (brandDraft.colors?.includes(value)) {
      message.error("Color already exists");
      return;
    }
    updateBrandDraft({ colors: [...(brandDraft.colors || []), value] });
    setNewColor("");
  };

  const handleRemoveColor = (value) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    updateBrandDraft({
      colors: (brandDraft.colors || []).filter((c) => c !== value),
    });
  };

  const handleAddFont = () => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    const value = newFont.trim();
    if (!value) return;
    if (brandDraft.fonts?.includes(value)) {
      message.error("Font already exists");
      return;
    }
    updateBrandDraft({ fonts: [...(brandDraft.fonts || []), value] });
    setNewFont("");
  };

  const handleRemoveFont = (value) => {
    if (!canEditKnowledge) {
      messageApi.error("This workspace is read-only for your role.");
      return;
    }
    updateBrandDraft({
      fonts: (brandDraft.fonts || []).filter((f) => f !== value),
    });
  };

  usePasteFiles({
    ref: brandAssetPasteRef,
    disabled: !isWorkspaceReady || !canEditKnowledge,
    onFiles: (files) => files.forEach(handleAddBrandAsset),
  });
  usePasteFiles({
    ref: folderImagesPasteRef,
    disabled: !isWorkspaceReady || !canEditKnowledge,
    onFiles: (files) => files.forEach(handleAddFolderImage),
  });
  usePasteFiles({
    ref: editImagesPasteRef,
    disabled: !isWorkspaceReady || !canEditKnowledge,
    onFiles: (files) => files.forEach(handleAddEditImage),
  });
  usePasteFiles({
    ref: analysisDocsPasteRef,
    disabled: !isWorkspaceReady || !canEditKnowledge,
    onFiles: (files) => handleAddAnalysisDocs(files),
  });
  usePasteFiles({
    ref: adCopyDocsPasteRef,
    disabled: !isWorkspaceReady || !canEditKnowledge,
    onFiles: (files) => files.forEach(handleAddAdCopyDoc),
  });
  usePasteFiles({
    ref: blogDocsPasteRef,
    disabled: !isWorkspaceReady || !canEditKnowledge,
    onFiles: (files) => files.forEach(handleAddBlogDoc),
  });
  usePasteFiles({
    ref: postTypeDocsPasteRef,
    disabled: !isWorkspaceReady || !canEditKnowledge,
    onFiles: (files) => files.forEach(handleAddPostTypeTagDoc),
  });
  usePasteFiles({
    ref: postTypeEditDocsPasteRef,
    disabled: !isWorkspaceReady || !canEditKnowledge,
    onFiles: (files) => files.forEach(handleAddPostTypeTagEditDoc),
  });
  usePasteFiles({
    ref: postExamplePasteRef,
    disabled: !isWorkspaceReady || !canEditKnowledge,
    allowedTypes: ["image/", "video/"],
    onFiles: (files) => files.forEach(handleAddPostExampleImage),
  });
  usePasteFiles({
    ref: postExampleEditPasteRef,
    disabled: !isWorkspaceReady || !canEditKnowledge,
    allowedTypes: ["image/", "video/"],
    onFiles: (files) => files.forEach(handleAddPostExampleEditImage),
  });

  const renderApiKeyStatusTags = (statusMap) => (
    <Space wrap>
      {[
        ["openai", "OpenAI"],
        ["gemini", "Gemini"],
        ["claude", "Claude"],
      ].map(([field, label]) => {
        const configured = Boolean(statusMap?.[field]);
        return (
          <Tag key={field} color={configured ? "green" : "default"}>
            {label}: {configured ? "Configured" : "Missing"}
          </Tag>
        );
      })}
    </Space>
  );

  const apiTabItems = [
    {
      key: "personal",
      label: "Personal",
      children: (
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Alert
            type="info"
            showIcon
            message="Stored keys are hidden"
            description="Saved API keys are never sent back to the browser. Enter a new key to rotate it."
          />
          {renderApiKeyStatusTags(resolvedApiKeyStatus)}
          {[
            { field: "openai", label: "OpenAI API Key", placeholder: "sk-...", tooltip: "Required for GPT-5.4 analysis/generation and GPT image generation" },
            { field: "gemini", label: "Gemini API Key", placeholder: "AIza...", tooltip: "Required for Gemini video analysis, Veo video generation, and Gemini image generation" },
            { field: "claude", label: "Claude API Key", placeholder: "sk-ant-...", tooltip: "Optional: Claude/Anthropic access for analysis or prompts" },
            { field: "huggingface", label: "HuggingFace Access Token", placeholder: "hf_...", tooltip: "Optional: required for Tribe v2 neuromarketing brain scan analysis" },
          ].map(({ field, label, placeholder, tooltip }) => (
            <Form.Item
              key={field}
              name={field}
              tooltip={tooltip}
              label={
                <Space size={6}>
                  {label}
                  {resolvedApiKeyStatus[field] && (
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      loading={personalKeysSaving}
                      onClick={(e) => { e.preventDefault(); handleClearSinglePersonalKey(field); }}
                      style={{ fontSize: 11, height: 20, padding: "0 6px" }}
                    >
                      Clear
                    </Button>
                  )}
                </Space>
              }
            >
              <Input.Password
                placeholder={resolvedApiKeyStatus[field] ? "Stored securely (enter new key to rotate)" : placeholder}
                autoComplete="new-password"
              />
            </Form.Item>
          ))}
          <Divider orientation="left" plain style={{ margin: "4px 0 8px" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Tribe v2 Brain Scan Server</Text>
          </Divider>
          {[
            { field: "tribe_server_url", label: "Tribe Server URL", placeholder: "https://...modal.run", tooltip: "The Modal endpoint URL for your Tribe v2 GPU server", isUrl: true },
            { field: "tribe_api_key", label: "Tribe Server API Key", placeholder: "Optional — leave blank if no auth", tooltip: "Optional: Bearer token if your Tribe server requires authentication" },
          ].map(({ field, label, placeholder, tooltip, isUrl }) => (
            <Form.Item
              key={field}
              name={field}
              tooltip={tooltip}
              label={
                <Space size={6}>
                  {label}
                  {resolvedApiKeyStatus[field] && (
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      loading={personalKeysSaving}
                      onClick={(e) => { e.preventDefault(); handleClearSinglePersonalKey(field); }}
                      style={{ fontSize: 11, height: 20, padding: "0 6px" }}
                    >
                      Clear
                    </Button>
                  )}
                </Space>
              }
            >
              {isUrl ? (
                <Input placeholder={resolvedApiKeyStatus[field] ? "Stored (enter new URL to update)" : placeholder} autoComplete="off" />
              ) : (
                <Input.Password placeholder={resolvedApiKeyStatus[field] ? "Stored securely (enter new key to rotate)" : placeholder} autoComplete="new-password" />
              )}
            </Form.Item>
          ))}
          <Divider orientation="left" plain style={{ margin: "4px 0 8px" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Video — AtlasCloud (Seedance)</Text>
          </Divider>
          <Form.Item
            name="atlascloud"
            tooltip="Required for Seedance video generation. Get your key at atlascloud.ai"
            label={
              <Space size={6}>
                AtlasCloud API Key
                {resolvedApiKeyStatus.atlascloud && (
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    loading={personalKeysSaving}
                    onClick={(e) => { e.preventDefault(); handleClearSinglePersonalKey("atlascloud"); }}
                    style={{ fontSize: 11, height: 20, padding: "0 6px" }}
                  >
                    Clear
                  </Button>
                )}
              </Space>
            }
          >
            <Input.Password
              placeholder={resolvedApiKeyStatus.atlascloud ? "Stored securely (enter new key to rotate)" : "sk-atlas-..."}
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={personalKeysSaving}
                block
              >
                Save API Keys
              </Button>
              <Popconfirm
                title="Clear all personal API keys?"
                description="This removes all stored personal provider keys."
                onConfirm={handleClearPersonalApiKeys}
                okText="Clear keys"
                cancelText="Cancel"
              >
                <Button
                  danger
                  loading={personalKeysSaving}
                  disabled={!Object.values(resolvedApiKeyStatus).some(Boolean)}
                  block
                >
                  Clear Stored Personal Keys
                </Button>
              </Popconfirm>
            </Space>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "workspace",
      label: "Workspace",
      children: isBusinessTier ? (
        <Card size="small" className="panel-card">
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Text strong>Workspace API Keys</Text>
            <Text type="secondary">
              Select a workspace and assign keys. Owners and admins can manage
              workspace keys.
            </Text>
            <Alert
              type="info"
              showIcon
              message="Workspace keys are hidden"
              description="Stored workspace keys are never returned to the browser. Enter a new key to rotate it."
            />
            {renderApiKeyStatusTags(resolvedWorkspaceApiKeyStatus)}
            {!canManageWorkspace && (
              <Text type="secondary">
                Stored key status is hidden for your role. Only owners/admins can rotate or clear workspace keys.
              </Text>
            )}
            <Select
              value={activeWorkspaceId}
              loading={workspaceLoading}
              onChange={handleSetActiveWorkspace}
              options={workspaceList.map((item) => ({
                value: item.id,
                label: `${item.name} (${item.isPersonal ? "personal" : "team"} • ${item.role})`,
              }))}
              placeholder="Select workspace"
            />
            <Form layout="vertical">
              <Form.Item label={
                <Space size={6}>
                  Workspace OpenAI API Key
                  {resolvedWorkspaceApiKeyStatus.openai && canManageWorkspace && (
                    <Button size="small" danger icon={<DeleteOutlined />} loading={workspaceKeysSaving} onClick={() => handleClearSingleWorkspaceKey("openai")} style={{ fontSize: 11, height: 20, padding: "0 6px" }}>Clear</Button>
                  )}
                </Space>
              }>
                <Input.Password
                  placeholder={
                    resolvedWorkspaceApiKeyStatus.openai
                      ? "Stored securely (enter new key to rotate)"
                      : "sk-..."
                  }
                  value={workspaceKeysDraft.openai}
                  onChange={(event) =>
                    setWorkspaceKeysDraft((prev) => ({
                      ...prev,
                      openai: event.target.value,
                    }))
                  }
                  disabled={!canManageWorkspace}
                  autoComplete="new-password"
                />
              </Form.Item>
              <Form.Item label={<Space size={6}>Workspace Gemini API Key{resolvedWorkspaceApiKeyStatus.gemini && canManageWorkspace && (<Button size="small" danger icon={<DeleteOutlined />} loading={workspaceKeysSaving} onClick={() => handleClearSingleWorkspaceKey("gemini")} style={{ fontSize: 11, height: 20, padding: "0 6px" }}>Clear</Button>)}</Space>}>
                <Input.Password
                  placeholder={
                    resolvedWorkspaceApiKeyStatus.gemini
                      ? "Stored securely (enter new key to rotate)"
                      : "AIza..."
                  }
                  value={workspaceKeysDraft.gemini}
                  onChange={(event) =>
                    setWorkspaceKeysDraft((prev) => ({
                      ...prev,
                      gemini: event.target.value,
                    }))
                  }
                  disabled={!canManageWorkspace}
                  autoComplete="new-password"
                />
              </Form.Item>
              <Form.Item label={<Space size={6}>Workspace Claude API Key{resolvedWorkspaceApiKeyStatus.claude && canManageWorkspace && (<Button size="small" danger icon={<DeleteOutlined />} loading={workspaceKeysSaving} onClick={() => handleClearSingleWorkspaceKey("claude")} style={{ fontSize: 11, height: 20, padding: "0 6px" }}>Clear</Button>)}</Space>}>
                <Input.Password
                  placeholder={
                    resolvedWorkspaceApiKeyStatus.claude
                      ? "Stored securely (enter new key to rotate)"
                      : "sk-ant-..."
                  }
                  value={workspaceKeysDraft.claude}
                  onChange={(event) =>
                    setWorkspaceKeysDraft((prev) => ({
                      ...prev,
                      claude: event.target.value,
                    }))
                  }
                  disabled={!canManageWorkspace}
                  autoComplete="new-password"
                />
              </Form.Item>
              <Form.Item label={<Space size={6}>Workspace HuggingFace Access Token{resolvedWorkspaceApiKeyStatus.huggingface && canManageWorkspace && (<Button size="small" danger icon={<DeleteOutlined />} loading={workspaceKeysSaving} onClick={() => handleClearSingleWorkspaceKey("huggingface")} style={{ fontSize: 11, height: 20, padding: "0 6px" }}>Clear</Button>)}</Space>}>
                <Input.Password
                  placeholder={
                    resolvedWorkspaceApiKeyStatus.huggingface
                      ? "Stored securely (enter new key to rotate)"
                      : "hf_..."
                  }
                  value={workspaceKeysDraft.huggingface}
                  onChange={(event) =>
                    setWorkspaceKeysDraft((prev) => ({
                      ...prev,
                      huggingface: event.target.value,
                    }))
                  }
                  disabled={!canManageWorkspace}
                  autoComplete="new-password"
                />
              </Form.Item>
              <Divider orientation="left" plain style={{ margin: "4px 0 8px" }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Tribe v2 Brain Scan Server</Text>
              </Divider>
              <Form.Item label={<Space size={6}>Workspace Tribe Server URL{resolvedWorkspaceApiKeyStatus.tribe_server_url && canManageWorkspace && (<Button size="small" danger icon={<DeleteOutlined />} loading={workspaceKeysSaving} onClick={() => handleClearSingleWorkspaceKey("tribe_server_url")} style={{ fontSize: 11, height: 20, padding: "0 6px" }}>Clear</Button>)}</Space>}>
                <Input
                  placeholder={
                    resolvedWorkspaceApiKeyStatus.tribe_server_url
                      ? "Stored (enter new URL to update)"
                      : "https://...modal.run"
                  }
                  value={workspaceKeysDraft.tribe_server_url}
                  onChange={(event) =>
                    setWorkspaceKeysDraft((prev) => ({
                      ...prev,
                      tribe_server_url: event.target.value,
                    }))
                  }
                  disabled={!canManageWorkspace}
                  autoComplete="off"
                />
              </Form.Item>
              <Form.Item label={<Space size={6}>Workspace Tribe Server API Key{resolvedWorkspaceApiKeyStatus.tribe_api_key && canManageWorkspace && (<Button size="small" danger icon={<DeleteOutlined />} loading={workspaceKeysSaving} onClick={() => handleClearSingleWorkspaceKey("tribe_api_key")} style={{ fontSize: 11, height: 20, padding: "0 6px" }}>Clear</Button>)}</Space>}>
                <Input.Password
                  placeholder={
                    resolvedWorkspaceApiKeyStatus.tribe_api_key
                      ? "Stored securely (enter new key to rotate)"
                      : "Optional — leave blank if no auth"
                  }
                  value={workspaceKeysDraft.tribe_api_key}
                  onChange={(event) =>
                    setWorkspaceKeysDraft((prev) => ({
                      ...prev,
                      tribe_api_key: event.target.value,
                    }))
                  }
                  disabled={!canManageWorkspace}
                  autoComplete="new-password"
                />
              </Form.Item>
              <Divider orientation="left" plain style={{ margin: "4px 0 8px" }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Video — AtlasCloud (Seedance)</Text>
              </Divider>
              <Form.Item label={<Space size={6}>Workspace AtlasCloud API Key{resolvedWorkspaceApiKeyStatus.atlascloud && canManageWorkspace && (<Button size="small" danger icon={<DeleteOutlined />} loading={workspaceKeysSaving} onClick={() => handleClearSingleWorkspaceKey("atlascloud")} style={{ fontSize: 11, height: 20, padding: "0 6px" }}>Clear</Button>)}</Space>}>
                <Input.Password
                  placeholder={
                    resolvedWorkspaceApiKeyStatus.atlascloud
                      ? "Stored securely (enter new key to rotate)"
                      : "sk-atlas-..."
                  }
                  value={workspaceKeysDraft.atlascloud}
                  onChange={(event) =>
                    setWorkspaceKeysDraft((prev) => ({
                      ...prev,
                      atlascloud: event.target.value,
                    }))
                  }
                  disabled={!canManageWorkspace}
                  autoComplete="new-password"
                />
              </Form.Item>
              <Form.Item>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => handleSaveWorkspaceApiKeys()}
                    loading={workspaceKeysSaving}
                    disabled={!canManageWorkspace}
                    block
                  >
                    Save Workspace Keys
                  </Button>
                  <Popconfirm
                    title="Clear all workspace API keys?"
                    description="This removes all stored provider keys for this workspace."
                    onConfirm={handleClearWorkspaceApiKeys}
                    okText="Clear keys"
                    cancelText="Cancel"
                  >
                    <Button
                      danger
                      loading={workspaceKeysSaving}
                      disabled={
                        !canManageWorkspace ||
                        !Object.values(resolvedWorkspaceApiKeyStatus).some(Boolean)
                      }
                      block
                    >
                      Clear Stored Workspace Keys
                    </Button>
                  </Popconfirm>
                </Space>
              </Form.Item>
            </Form>
          </Space>
        </Card>
      ) : (
        <Alert
          type="info"
          showIcon
          message="Business plan feature"
          description="Workspace API keys are available on the Business plan."
        />
      ),
    },
  ];

  const apiTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <KeyOutlined /> API Keys
        </Title>
        <Text type="secondary">
          Manage personal keys or assign API keys to a specific workspace.
        </Text>
      </div>
      <Tabs className="settings-subtabs" size="small" items={apiTabItems} />
    </Space>
  );

  const brandTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <AppstoreOutlined /> Brand Library
        </Title>
        <Text type="secondary">
          Store brand assets and guidelines to influence image generation.
        </Text>
      </div>
      <Input
        placeholder="Brand name"
        value={brandDraft.name}
        onChange={(event) => updateBrandDraft({ name: event.target.value })}
        disabled={!canEditKnowledge}
      />
      <Input.TextArea
        rows={3}
        placeholder="Brand message / positioning"
        value={brandDraft.message}
        onChange={(event) => updateBrandDraft({ message: event.target.value })}
        disabled={!canEditKnowledge}
      />
      <Space direction="vertical" size="small">
        <Text strong>Brand Colors</Text>
        <Space wrap>
          <Input
            placeholder="Add color (e.g. #111111 or Charcoal)"
            value={newColor}
            onChange={(event) => setNewColor(event.target.value)}
            style={{ width: 240 }}
            disabled={!canEditKnowledge}
            suffix={
              <Input
                type="color"
                className="color-picker-input"
                value={
                  /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(newColor)
                    ? newColor
                    : "#111111"
                }
                onChange={(event) => setNewColor(event.target.value)}
                disabled={!canEditKnowledge}
              />
            }
          />
          <Button onClick={handleAddColor} disabled={!canEditKnowledge}>
            Add
          </Button>
        </Space>
        <Space size={8} wrap>
          {(brandDraft.colors || []).map((color) => (
            <Tag
              key={color}
              closable={canEditKnowledge}
              onClose={() => handleRemoveColor(color)}
            >
              <span className="color-swatch" style={{ background: color }} />
              {color}
            </Tag>
          ))}
        </Space>
      </Space>
      <Space direction="vertical" size="small">
        <Text strong>Fonts</Text>
        <Space>
          <Input
            placeholder="Add font family"
            value={newFont}
            onChange={(event) => setNewFont(event.target.value)}
            disabled={!canEditKnowledge}
          />
          <Button onClick={handleAddFont} disabled={!canEditKnowledge}>
            Add
          </Button>
        </Space>
        <Space size={8} wrap>
          {(brandDraft.fonts || []).map((font) => (
            <Tag
              key={font}
              closable={canEditKnowledge}
              onClose={() => handleRemoveFont(font)}
            >
              {font}
            </Tag>
          ))}
        </Space>
      </Space>
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Text strong>Brand Assets</Text>
        <div
          ref={brandAssetPasteRef}
          tabIndex={0}
          className="paste-upload-area"
        >
          <Upload
            accept="image/*"
            showUploadList={false}
            disabled={!isWorkspaceReady || !canEditKnowledge}
            beforeUpload={(file) => {
              handleAddBrandAsset(file);
              return false;
            }}
          >
            <Button
              icon={<UploadOutlined />}
              disabled={!isWorkspaceReady || !canEditKnowledge}
            >
              Add Brand Asset
            </Button>
          </Upload>
        </div>
        {brandDraft.assets && brandDraft.assets.length > 0 ? (
          <div className="brand-assets-grid">
            {brandDraft.assets.map((asset) => (
              <div key={asset.id} className="brand-asset-tile">
                <div className="brand-asset-media-wrap">
                  <img
                    src={asset.url || asset.dataUrl}
                    alt={asset.name}
                    className="brand-asset-media"
                  />
                  <Tooltip title="Delete asset">
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      className="brand-asset-delete"
                      onClick={() => handleRemoveBrandAsset(asset.id)}
                      disabled={!canEditKnowledge}
                    />
                  </Tooltip>
                  <Tooltip title="Edit file name">
                    <Button
                      size="small"
                      type="text"
                      icon={<EditOutlined />}
                      className="brand-asset-edit"
                      onClick={() => handleOpenBrandAssetRename(asset)}
                      disabled={!canEditKnowledge}
                    />
                  </Tooltip>
                </div>
                <Text ellipsis={{ tooltip: asset.name || "" }}>
                  {asset.name}
                </Text>
              </div>
            ))}
          </div>
        ) : (
          <Text type="secondary">No assets yet.</Text>
        )}
      </Space>
      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSaveBrand}
        disabled={!canEditKnowledge}
      >
        Save Brand Library
      </Button>
      <Modal
        title="Edit file name"
        open={!!editingBrandAsset}
        onCancel={handleCloseBrandAssetRename}
        onOk={handleSaveBrandAssetRename}
        okText="Save"
        okButtonProps={{ disabled: !canEditKnowledge }}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Input
            value={brandAssetBaseNameDraft}
            placeholder="File name"
            onChange={(event) => setBrandAssetBaseNameDraft(event.target.value)}
            onPressEnter={handleSaveBrandAssetRename}
            disabled={!canEditKnowledge}
          />
          <Text type="secondary">
            File type is locked:{" "}
            {splitFileName(editingBrandAsset?.name || "").ext ||
              "(no extension)"}
          </Text>
        </Space>
      </Modal>
    </Space>
  );

  const aiTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <RobotOutlined /> AI Instructions
        </Title>
        <Text type="secondary">
          Shape how the AI thinks and writes. These instructions are injected
          into analysis, variant generation, and image prompts.
        </Text>
      </div>
      <Input.TextArea
        rows={4}
        placeholder="Custom instructions (e.g. prioritize premium composition, avoid clutter, emphasize product authenticity)."
        value={aiProfileState.instructions}
        onChange={(event) =>
          setAiProfileState((prev) => ({
            ...prev,
            instructions: event.target.value,
          }))
        }
        disabled={!canEditKnowledge}
      />
      <Input.TextArea
        rows={3}
        placeholder="Personality / tone (e.g. decisive, punchy, brand-safe, concise)."
        value={aiProfileState.personality}
        onChange={(event) =>
          setAiProfileState((prev) => ({
            ...prev,
            personality: event.target.value,
          }))
        }
        disabled={!canEditKnowledge}
      />
      <Input.TextArea
        rows={3}
        placeholder="Must-do guidance (e.g. always highlight craftsmanship, always describe camera lens)."
        value={aiProfileState.mustDo}
        onChange={(event) =>
          setAiProfileState((prev) => ({ ...prev, mustDo: event.target.value }))
        }
        disabled={!canEditKnowledge}
      />
      <Input.TextArea
        rows={3}
        placeholder="Avoid / do-not (e.g. no discount language, no clichÃ©s)."
        value={aiProfileState.avoid}
        onChange={(event) =>
          setAiProfileState((prev) => ({ ...prev, avoid: event.target.value }))
        }
        disabled={!canEditKnowledge}
      />
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Text strong>
          <LinkOutlined /> Reference URLs
        </Text>
        <Text type="secondary">
          The AI will use these as reference context in prompts.
        </Text>
        <Space style={{ width: "100%" }}>
          <Input
            placeholder="https://yourbrand.com/guide"
            value={aiUrlInput}
            onChange={(event) => setAiUrlInput(event.target.value)}
            disabled={!canEditKnowledge}
          />
          <Button onClick={handleAddAiUrl} disabled={!canEditKnowledge}>
            Add
          </Button>
        </Space>
        <Space size={8} wrap>
          {(aiProfileState.referenceUrls || []).map((url) => (
            <Tag
              key={url}
              closable={canEditKnowledge}
              onClose={() => handleRemoveAiUrl(url)}
            >
              {url}
            </Tag>
          ))}
        </Space>
      </Space>
      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSaveAiProfile}
        disabled={!canEditKnowledge}
      >
        Save AI Instructions
      </Button>
    </Space>
  );

  const MODEL_DEFAULT = "__default__";
  const resolveModelLabel = (value) => {
    if (!value) return "Default";
    return getModelLabel(normalizeModelSelection(value, value));
  };
  const postDefaultModel = DEFAULT_POST_ANALYSIS_MODEL;
  const resolvedPostModel = normalizeModelSelection(
    postAnalysisModel,
    postDefaultModel,
  );
  const postModelSelectValue = postAnalysisModel
    ? postAnalysisModel
    : MODEL_DEFAULT;
  const postModelOptions = [
    { value: MODEL_DEFAULT, label: "Default" },
    ...TEXT_MODEL_OPTIONS,
  ];
  const blogDefaultModel = DEFAULT_BLOG_MODEL;
  const resolvedBlogModel = normalizeModelSelection(blogModel, blogDefaultModel);
  const blogModelSelectValue = blogModel ? blogModel : MODEL_DEFAULT;
  const blogModelOptions = [
    { value: MODEL_DEFAULT, label: "Default" },
    ...TEXT_MODEL_OPTIONS,
  ];
  const preferencesTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <SettingOutlined /> Personal Preferences
        </Title>
        <Text type="secondary">
          Preferences are saved per user and are not shared with the workspace.
        </Text>
      </div>
      <Card size="small" className="panel-card">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text strong>Post Analysis Model</Text>
          <Text type="secondary">
            Used for image analysis in the Post page. Current:{" "}
            {resolveModelLabel(resolvedPostModel)}.
          </Text>
          <Select
            value={postModelSelectValue}
            onChange={(value) => onPostAnalysisModelChange?.(value === MODEL_DEFAULT ? "" : value)}
            options={postModelOptions}
            style={{ width: "100%" }}
          />
        </Space>
      </Card>
      <Card size="small" className="panel-card">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text strong>Blog Writing Model</Text>
          <Text type="secondary">
            Used for blog drafts and revisions. Current:{" "}
            {resolveModelLabel(resolvedBlogModel)}.
          </Text>
          <Select
            value={blogModelSelectValue}
            onChange={(value) => onBlogModelChange?.(value === MODEL_DEFAULT ? "" : value)}
            options={blogModelOptions}
            style={{ width: "100%" }}
          />
        </Space>
      </Card>
    </Space>
  );

  const creativeKnowledgeTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Tooltip title="Product image knowledge powers reference selection, visual comparisons, and prompt grounding during analysis and generation. Add product folders and images to guide the AI toward your preferred visual language.">
        <Text type="secondary">
          Add product folders with images and notes to ground the AI with your
          product visuals and reference material.
        </Text>
      </Tooltip>
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Input
          placeholder="Folder name (e.g. Trimmer, Bundle, Aftershave)"
          value={folderName}
          onChange={(event) => setFolderName(event.target.value)}
        />
        <Input.TextArea
          rows={3}
          placeholder="Optional product notes (materials, finishes, packaging, signature elements)"
          value={folderNotes}
          onChange={(event) => setFolderNotes(event.target.value)}
        />
        <div
          ref={folderImagesPasteRef}
          tabIndex={0}
          className="paste-upload-area"
        >
          <Upload
            accept="image/*"
            multiple
            showUploadList={false}
            disabled={!isWorkspaceReady || !canEditKnowledge}
            beforeUpload={(file) => {
              handleAddFolderImage(file);
              return false;
            }}
          >
            <Button
              icon={<UploadOutlined />}
              disabled={!isWorkspaceReady || !canEditKnowledge}
            >
              Add Reference Images
            </Button>
          </Upload>
        </div>
        {folderImages.length > 0 && (
          <Space size={8} wrap>
            {folderImages.map((img) => (
              <div
                key={img.id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  padding: 4,
                  width: 64,
                  textAlign: "center",
                }}
              >
                <img
                  src={img.url || img.dataUrl}
                  alt={img.name}
                  style={{
                    width: "100%",
                    height: 48,
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />
                <Input
                  size="small"
                  placeholder="Shot name"
                  value={img.name}
                  onChange={(event) =>
                    handleFolderImageName(img.id, event.target.value)
                  }
                />
                <Button
                  size="small"
                  type="link"
                  onClick={() => handleRemoveFolderImage(img.id)}
                >
                  Remove
                </Button>
              </div>
            ))}
          </Space>
        )}
        <Button
          type="primary"
          icon={<FolderAddOutlined />}
          onClick={handleAddFolder}
          disabled={!canEditKnowledge}
          block
        >
          Add Folder
        </Button>
      </Space>
      <List
        bordered
        dataSource={knowledgeDb}
        locale={{ emptyText: "No folders yet." }}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Tooltip key="edit" title="Edit folder">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => openEditFolder(item)}
                  disabled={!canEditKnowledge}
                />
              </Tooltip>,
              <Popconfirm
                key="delete"
                title="Delete this folder?"
                onConfirm={() => handleDeleteFolder(item.id)}
                okText="Delete"
                cancelText="Cancel"
                disabled={!canEditKnowledge}
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!canEditKnowledge}
                />
              </Popconfirm>,
            ]}
          >
            <List.Item.Meta
              title={item.name}
              description={
                <Space direction="vertical" size={4}>
                  <Text type="secondary">{item.notes || "No notes"}</Text>
                  {item.images && item.images.length > 0 ? (
                    <Space size={8} wrap>
                      {item.images.map((img) => (
                        <div
                          key={img.id}
                          style={{
                            border: "1px solid #f0f0f0",
                            borderRadius: 6,
                            padding: 4,
                            width: 84,
                            textAlign: "center",
                          }}
                        >
                          <img
                            src={img.url || img.dataUrl}
                            alt={img.name}
                            style={{
                              width: "100%",
                              height: 48,
                              objectFit: "cover",
                              borderRadius: 4,
                            }}
                          />
                          <Text
                            style={{
                              fontSize: 11,
                              maxWidth: 72,
                              display: "block",
                            }}
                            ellipsis={{ tooltip: img.name || "Shot" }}
                          >
                            {img.name || "Shot"}
                          </Text>
                        </div>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">No images yet.</Text>
                  )}
                </Space>
              }
            />
          </List.Item>
        )}
      />
      <Modal
        title="Edit Folder"
        open={!!editingFolder}
        onCancel={closeEditFolder}
        onOk={handleSaveEdit}
        okText="Save"
        okButtonProps={{ disabled: !canEditKnowledge }}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Input
            placeholder="Folder name"
            value={editName}
            onChange={(event) => setEditName(event.target.value)}
          />
          <Input.TextArea
            rows={3}
            placeholder="Folder notes"
            value={editNotes}
            onChange={(event) => setEditNotes(event.target.value)}
          />
          <div
            ref={editImagesPasteRef}
            tabIndex={0}
            className="paste-upload-area"
          >
            <Upload
              accept="image/*"
              multiple
              showUploadList={false}
              disabled={!isWorkspaceReady || !canEditKnowledge}
              beforeUpload={(file) => {
                handleAddEditImage(file);
                return false;
              }}
            >
              <Button
                icon={<UploadOutlined />}
                disabled={!isWorkspaceReady || !canEditKnowledge}
              >
                Add Images
              </Button>
            </Upload>
          </div>
          {editImages.length > 0 ? (
            <Space size={8} wrap>
              {editImages.map((img) => (
                <div
                  key={img.id}
                  style={{
                    border: "1px solid #f0f0f0",
                    borderRadius: 6,
                    padding: 4,
                    width: 120,
                  }}
                >
                  <img
                    src={img.url || img.dataUrl}
                    alt={img.name}
                    style={{
                      width: "100%",
                      height: 64,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                  <Input
                    size="small"
                    placeholder="Shot name"
                    value={img.name}
                    onChange={(event) =>
                      handleEditImageName(img.id, event.target.value)
                    }
                  />
                  <Button
                    size="small"
                    type="link"
                    onClick={() => handleRemoveEditImage(img.id)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </Space>
          ) : (
            <Text type="secondary">No images yet.</Text>
          )}
        </Space>
      </Modal>
    </Space>
  );

  const analysisKnowledgeTab = (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Tooltip title="General knowledge feeds all AI features — analysis, generation, copy, and blog. Add notes and reference documents to shape how the AI understands your brand, products, and guidelines.">
        <Text type="secondary">
          Add general notes and reference documents to guide the AI across all
          features — analysis, generation, copy, and blog.
        </Text>
      </Tooltip>
      <Input.TextArea
        rows={6}
        placeholder="Add general knowledge notes (brand guidelines, product details, audience insights, tone of voice, do's and don'ts)."
        value={analysisNotes}
        onChange={(event) => setAnalysisNotes(event.target.value)}
        disabled={!canEditKnowledge}
      />
      <div
        ref={analysisDocsPasteRef}
        tabIndex={0}
        className="paste-upload-area"
      >
        <Upload
          accept=".pdf,.txt,.md,.json,.csv,.doc,.docx"
          multiple
          showUploadList={false}
          disabled={
            !isWorkspaceReady ||
            !canEditKnowledge ||
            analysisUploadState.active
          }
          beforeUpload={(file, fileList) => {
            const batch =
              Array.isArray(fileList) && fileList.length > 0
                ? fileList
                : [file];
            const firstUid = batch?.[0]?.uid;
            if (firstUid && file?.uid && file.uid !== firstUid) {
              return false;
            }
            handleAddAnalysisDocs(batch);
            return false;
          }}
        >
          <Button
            icon={<UploadOutlined />}
            loading={analysisUploadState.active}
            disabled={
              !isWorkspaceReady ||
              !canEditKnowledge ||
              analysisUploadState.active
            }
          >
            Add Reference Documents
          </Button>
        </Upload>
      </div>
      <Text type="secondary">
        Limits: {ANALYSIS_UPLOAD_LIMITS.maxFilesPerBatch} files per batch,{" "}
        {formatBytes(ANALYSIS_UPLOAD_LIMITS.maxFileSizeBytes)} max each,{" "}
        {ANALYSIS_UPLOAD_LIMITS.maxTotalDocs} total references,{" "}
        {ANALYSIS_UPLOAD_LIMITS.maxConcurrent} concurrent uploads.
      </Text>
      {analysisUploadState.active ? (
        <Text type="secondary">
          Uploading {analysisUploadState.completed}/{analysisUploadState.total}
          {analysisUploadState.failed > 0
            ? ` • ${analysisUploadState.failed} failed`
            : ""}
          {analysisUploadState.skipped > 0
            ? ` • ${analysisUploadState.skipped} skipped`
            : ""}
        </Text>
      ) : null}
      {analysisDocs.length > 0 ? (
        <List
          size="small"
          bordered
          dataSource={analysisDocs}
          renderItem={(doc) => (
            <List.Item
              actions={[
                <Button
                  key="remove"
                  type="link"
                  onClick={() => handleRemoveAnalysisDoc(doc.id)}
                  disabled={!canEditKnowledge}
                >
                  Remove
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={doc.name}
                description={
                  doc.size
                    ? `${formatBytes(doc.size)} • ${doc.mimeType || "file"}`
                    : doc.mimeType || "file"
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Text type="secondary">No reference documents yet.</Text>
      )}
      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSaveAnalysis}
        disabled={!canEditKnowledge}
        block
      >
        Save General Knowledge
      </Button>
    </Space>
  );

  const adCopyKnowledgeTab = (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Tooltip title="Copy knowledge powers headline and primary text generation. Provide tone, claims, and compliance notes so the AI writes on-brand ad copy.">
        <Text type="secondary">
          Copy knowledge drives ad copy generation for headlines, primary text,
          and messaging guidance.
        </Text>
      </Tooltip>
      <Input.TextArea
        rows={3}
        placeholder="Add ad copy guidance (tone, claims to emphasize, compliance notes)."
        value={adCopy.notes || ""}
        onChange={(event) =>
          setAdCopy((prev) => ({ ...prev, notes: event.target.value }))
        }
        disabled={!canEditKnowledge}
      />
      <Input.TextArea
        rows={3}
        placeholder="Negative guidance to avoid repeating weak hooks, poor structure, low-clarity offers, or other losing patterns."
        value={adCopy.avoid || ""}
        onChange={(event) =>
          setAdCopy((prev) => ({ ...prev, avoid: event.target.value }))
        }
        disabled={!canEditKnowledge}
      />
      <div ref={adCopyDocsPasteRef} tabIndex={0} className="paste-upload-area">
        <Upload
          accept=".pdf,.txt,.md,.json,.csv,.doc,.docx"
          multiple
          showUploadList={false}
          disabled={!isWorkspaceReady || !canEditKnowledge}
          beforeUpload={(file) => {
            handleAddAdCopyDoc(file);
            return false;
          }}
        >
          <Button
            icon={<UploadOutlined />}
            disabled={!isWorkspaceReady || !canEditKnowledge}
          >
            Add Ad Copy References
          </Button>
        </Upload>
      </div>
      {adCopy.docs && adCopy.docs.length > 0 ? (
        <List
          size="small"
          bordered
          dataSource={adCopy.docs}
          renderItem={(doc) => (
            <List.Item
              actions={[
                <Button
                  key="remove"
                  type="link"
                  onClick={() => handleRemoveAdCopyDoc(doc.id)}
                  disabled={!canEditKnowledge}
                >
                  Remove
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={doc.name}
                description={
                  doc.size
                    ? `${formatBytes(doc.size)} • ${doc.mimeType || "file"}`
                    : doc.mimeType || "file"
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Text type="secondary">No ad copy references yet.</Text>
      )}
      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSaveAdCopy}
        disabled={!canEditKnowledge}
        block
      >
        Save Ad Copy Knowledge
      </Button>
    </Space>
  );

  const blogKnowledgeTab = (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Tooltip title="Blog knowledge shapes long-form content and imagery. These notes combine with creative, analysis, and copy knowledge.">
        <Text type="secondary">
          Blog knowledge guides structure, audience, SEO defaults, and on-brand
          voice for the Blog page.
        </Text>
      </Tooltip>
      <Divider orientation="left" plain>
        Strategy
      </Divider>
      <Input.TextArea
        rows={3}
        placeholder="Core blog guidance: voice, POV, brand narrative, positioning, and key messages."
        value={blogGuide.notes || ""}
        onChange={(event) =>
          setBlogGuide((prev) => ({ ...prev, notes: event.target.value }))
        }
        disabled={!canEditKnowledge}
      />
      <Input
        placeholder="Blog goals (e.g. educate, convert, rank for keyword)."
        value={blogGuide.goals || ""}
        onChange={(event) =>
          setBlogGuide((prev) => ({ ...prev, goals: event.target.value }))
        }
        disabled={!canEditKnowledge}
      />
      <Input
        placeholder="Target audience / persona"
        value={blogGuide.audience || ""}
        onChange={(event) =>
          setBlogGuide((prev) => ({ ...prev, audience: event.target.value }))
        }
        disabled={!canEditKnowledge}
      />
      <Input.TextArea
        rows={2}
        placeholder="Must-include points, products, or terms"
        value={blogGuide.mustInclude || ""}
        onChange={(event) =>
          setBlogGuide((prev) => ({ ...prev, mustInclude: event.target.value }))
        }
        disabled={!canEditKnowledge}
      />
      <Input.TextArea
        rows={2}
        placeholder="Avoid / compliance notes"
        value={blogGuide.avoid || ""}
        onChange={(event) =>
          setBlogGuide((prev) => ({ ...prev, avoid: event.target.value }))
        }
        disabled={!canEditKnowledge}
      />
      <Input.TextArea
        rows={2}
        placeholder="Default CTA guidance (optional)"
        value={blogGuide.cta || ""}
        onChange={(event) =>
          setBlogGuide((prev) => ({ ...prev, cta: event.target.value }))
        }
        disabled={!canEditKnowledge}
      />
      <Divider orientation="left" plain>
        SEO Defaults
      </Divider>
      <Input
        placeholder="Default meta title pattern or preferred title style"
        value={blogSeoDraft.metaTitle || ""}
        onChange={(event) =>
          setBlogSeoDraft((prev) => ({
            ...prev,
            metaTitle: event.target.value,
          }))
        }
        disabled={!canEditKnowledge}
      />
      <Input.TextArea
        rows={2}
        placeholder="Default meta description angle or messaging"
        value={blogSeoDraft.metaDescription || ""}
        onChange={(event) =>
          setBlogSeoDraft((prev) => ({
            ...prev,
            metaDescription: event.target.value,
          }))
        }
        disabled={!canEditKnowledge}
      />
      <Input
        placeholder="Default keywords (comma separated)"
        value={(blogSeoDraft.keywords || []).join(", ")}
        onChange={(event) =>
          setBlogSeoDraft((prev) => ({
            ...prev,
            keywords: event.target.value
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean),
          }))
        }
        disabled={!canEditKnowledge}
      />
      <Input
        placeholder="Default slug hint (optional)"
        value={blogSeoDraft.slug || ""}
        onChange={(event) =>
          setBlogSeoDraft((prev) => ({
            ...prev,
            slug: event.target.value,
          }))
        }
        disabled={!canEditKnowledge}
      />
      <Input
        placeholder="Canonical URL base or preferred canonical"
        value={blogSeoDraft.canonicalUrl || ""}
        onChange={(event) =>
          setBlogSeoDraft((prev) => ({
            ...prev,
            canonicalUrl: event.target.value,
          }))
        }
        disabled={!canEditKnowledge}
      />
      <Input.TextArea
        rows={4}
        placeholder="Default schema markup template (JSON-LD, optional)"
        value={blogSeoDraft.schemaMarkup || ""}
        onChange={(event) =>
          setBlogSeoDraft((prev) => ({
            ...prev,
            schemaMarkup: event.target.value,
          }))
        }
        disabled={!canEditKnowledge}
      />
      <Divider orientation="left" plain>
        References
      </Divider>
      <div ref={blogDocsPasteRef} tabIndex={0} className="paste-upload-area">
        <Upload
          accept=".pdf,.txt,.md,.json,.csv,.doc,.docx"
          multiple
          showUploadList={false}
          disabled={!isWorkspaceReady || !canEditKnowledge}
          beforeUpload={(file) => {
            handleAddBlogDoc(file);
            return false;
          }}
        >
          <Button
            icon={<UploadOutlined />}
            disabled={!isWorkspaceReady || !canEditKnowledge}
          >
            Add Blog References
          </Button>
        </Upload>
      </div>
      {blogGuide.docs && blogGuide.docs.length > 0 ? (
        <List
          size="small"
          bordered
          dataSource={blogGuide.docs}
          renderItem={(doc) => (
            <List.Item
              actions={[
                <Button
                  key="remove"
                  type="link"
                  onClick={() => handleRemoveBlogDoc(doc.id)}
                  disabled={!canEditKnowledge}
                >
                  Remove
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={doc.name}
                description={
                  doc.size
                    ? `${formatBytes(doc.size)} • ${doc.mimeType || "file"}`
                    : doc.mimeType || "file"
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Text type="secondary">No blog references yet.</Text>
      )}
      <Text type="secondary" style={{ fontSize: 12 }}>
        Changes here auto-sync to the active workspace and prefill the Blog generator.
      </Text>
    </Space>
  );

  const knowledgeTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <DatabaseOutlined /> Knowledge Base
        </Title>
        <Text type="secondary">
          Organize assets and guidance that shape analysis and generation.
        </Text>
      </div>
      {!isWorkspaceReady && (
        <Alert
          type="warning"
          showIcon
          message="Workspace required"
          description="Select or create a workspace before uploading assets."
        />
      )}
      <Card size="small" className="panel-card">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text strong>Knowledge export + sync</Text>
          <Text type="secondary">
            Export/import all knowledge (product images, general knowledge,
            tags, brand, AI profile). This syncs to the active workspace and its
            members.
          </Text>
          <Space wrap>
            <Button
              icon={<CloudDownloadOutlined />}
              onClick={handleExportKnowledge}
            >
              Export all knowledge
            </Button>
            <Upload
              accept=".json"
              showUploadList={false}
              disabled={!canEditKnowledge}
              beforeUpload={(file) => {
                handleImportKnowledge(file);
                return false;
              }}
            >
              <Button
                icon={<UploadOutlined />}
                loading={importing}
                disabled={!canEditKnowledge}
              >
                Import knowledge
              </Button>
            </Upload>
          </Space>
        </Space>
      </Card>
      <Tabs
        size="small"
        className="settings-knowledge-tabs"
        items={[
          {
            key: "creative",
            label: "Product Images",
            children: creativeKnowledgeTab,
          },
          {
            key: "general",
            label: "General Knowledge",
            children: analysisKnowledgeTab,
          },
        ]}
      />
    </Space>
  );

  const tagsTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <TagOutlined /> Tags
        </Title>
        <Text type="secondary">
          Manage prompt tags for generation and post formats.
        </Text>
      </div>
      <Tabs
        className="settings-subtabs"
        size="small"
        items={[
          {
            key: "generation",
            label: "Generation Tags",
            children: (
              <Card size="small" className="panel-card">
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text strong>Generation Tags</Text>
                  <Text type="secondary">
                    Tags inject prompt details into image generation. Star up to
                    3 tags to pin them at the top of the Generate selector.
                  </Text>
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <Input
                      placeholder="Tag name (e.g. Humor, Luxury, Minimal)"
                      value={tagName}
                      onChange={(event) => setTagName(event.target.value)}
                      disabled={!canEditKnowledge}
                    />
                    <Input.TextArea
                      rows={3}
                      placeholder="Tag description (used in prompt)"
                      value={tagDescription}
                      onChange={(event) =>
                        setTagDescription(event.target.value)
                      }
                      disabled={!canEditKnowledge}
                    />
                    <Button
                      type="primary"
                      onClick={handleAddTag}
                      disabled={!canEditKnowledge}
                      block
                    >
                      Add Tag
                    </Button>
                  </Space>
                  <List
                    bordered
                    dataSource={tags}
                    locale={{ emptyText: "No tags yet." }}
                    renderItem={(tag) => (
                      <List.Item
                        actions={[
                          <Tooltip
                            key="star"
                            title={tag.starred ? "Unstar tag" : "Star tag"}
                          >
                            <Button
                              type="text"
                              icon={
                                tag.starred ? <StarFilled /> : <StarOutlined />
                              }
                              onClick={() => handleToggleTagStar(tag.id)}
                              disabled={!canEditKnowledge}
                            />
                          </Tooltip>,
                          <Tooltip key="edit" title="Edit tag">
                            <Button
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => openEditTag(tag)}
                              disabled={!canEditKnowledge}
                            />
                          </Tooltip>,
                          <Popconfirm
                            key="delete"
                            title="Delete this tag?"
                            onConfirm={() => handleDeleteTag(tag.id)}
                            okText="Delete"
                            cancelText="Cancel"
                            disabled={!canEditKnowledge}
                          >
                            <Button
                              danger
                              size="small"
                              disabled={!canEditKnowledge}
                            >
                              Delete
                            </Button>
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            <Space size={6}>
                              <span>{tag.name}</span>
                              {tag.starred && (
                                <StarFilled style={{ color: "#f59e0b" }} />
                              )}
                            </Space>
                          }
                          description={tag.description || "No description"}
                        />
                      </List.Item>
                    )}
                  />
                </Space>
              </Card>
            ),
          },
          {
            key: "posttypes",
            label: "Post Types",
            children: (
              <Card size="small" className="panel-card">
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text strong>Post Types</Text>
                  <Text type="secondary">
                    Define post styles for the Post page (ads, blogs, native
                    stories). Each tag sets the output structure.
                  </Text>
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <Input
                      placeholder="Post type name (e.g. Meta Ads, Blog, Native Story)"
                      value={postTypeTagName}
                      onChange={(event) =>
                        setPostTypeTagName(event.target.value)
                      }
                      disabled={!canEditKnowledge}
                    />
                    <Input.TextArea
                      rows={3}
                      placeholder="Post type description (used in prompt)"
                      value={postTypeTagDescription}
                      onChange={(event) =>
                        setPostTypeTagDescription(event.target.value)
                      }
                      disabled={!canEditKnowledge}
                    />
                    <Input.TextArea
                      rows={2}
                      placeholder="Extra info (optional notes for this post type)"
                      value={postTypeTagExtraInfo}
                      onChange={(event) =>
                        setPostTypeTagExtraInfo(event.target.value)
                      }
                      disabled={!canEditKnowledge}
                    />
                    <Select
                      value={postTypeTagFormat}
                      onChange={setPostTypeTagFormat}
                      options={postTypeFormatOptions}
                      disabled={!canEditKnowledge}
                      style={{ width: "100%" }}
                    />
                    <div
                      ref={postTypeDocsPasteRef}
                      tabIndex={0}
                      className="paste-upload-area"
                    >
                      <Upload
                        accept=".pdf,.txt,.md,.json,.csv,.doc,.docx"
                        multiple
                        showUploadList={false}
                        disabled={!isWorkspaceReady || !canEditKnowledge}
                        beforeUpload={(file) => {
                          handleAddPostTypeTagDoc(file);
                          return false;
                        }}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          disabled={!isWorkspaceReady || !canEditKnowledge}
                        >
                          Add Post Type References
                        </Button>
                      </Upload>
                    </div>
                    {postTypeTagDocs.length > 0 ? (
                      <List
                        size="small"
                        bordered
                        dataSource={postTypeTagDocs}
                        renderItem={(doc) => (
                          <List.Item
                            actions={[
                              <Button
                                key="remove"
                                type="link"
                                onClick={() =>
                                  handleRemovePostTypeTagDoc(doc.id)
                                }
                                disabled={!canEditKnowledge}
                              >
                                Remove
                              </Button>,
                            ]}
                          >
                            <List.Item.Meta
                              title={doc.name}
                              description={
                                doc.size
                                  ? `${formatBytes(doc.size)} • ${doc.mimeType || "file"}`
                                  : doc.mimeType || "file"
                              }
                            />
                          </List.Item>
                        )}
                      />
                    ) : (
                      <Text type="secondary">No post type references yet.</Text>
                    )}
                    <Button
                      type="primary"
                      onClick={handleAddPostTypeTag}
                      disabled={!canEditKnowledge}
                      block
                    >
                      Add Post Type
                    </Button>
                  </Space>
                  <List
                    bordered
                    dataSource={postTypeTags}
                    locale={{ emptyText: "No post types yet." }}
                    renderItem={(tag) => (
                      <List.Item
                        actions={[
                          <Tooltip
                            key="star"
                            title={
                              tag.starred
                                ? "Unstar post type"
                                : "Star post type"
                            }
                          >
                            <Button
                              type="text"
                              icon={
                                tag.starred ? <StarFilled /> : <StarOutlined />
                              }
                              onClick={() =>
                                handleTogglePostTypeTagStar(tag.id)
                              }
                              disabled={!canEditKnowledge}
                            />
                          </Tooltip>,
                          <Tooltip key="edit" title="Edit post type">
                            <Button
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => openEditPostTypeTag(tag)}
                              disabled={!canEditKnowledge}
                            />
                          </Tooltip>,
                          <Popconfirm
                            key="delete"
                            title="Delete this post type?"
                            onConfirm={() => handleDeletePostTypeTag(tag.id)}
                            okText="Delete"
                            cancelText="Cancel"
                            disabled={!canEditKnowledge}
                          >
                            <Button
                              danger
                              size="small"
                              disabled={!canEditKnowledge}
                            >
                              Delete
                            </Button>
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          title={tag.name}
                          description={
                            <Space direction="vertical" size={4}>
                              <Text type="secondary">
                                {tag.description || "No description"}
                              </Text>
                              {tag.extraInfo ? (
                                <Text type="secondary">{tag.extraInfo}</Text>
                              ) : null}
                              {Array.isArray(tag.docs) &&
                              tag.docs.length > 0 ? (
                                <Text type="secondary">
                                  References:{" "}
                                  {tag.docs.map((doc) => doc.name).join(", ")}
                                </Text>
                              ) : (
                                <Text type="secondary">No references</Text>
                              )}
                              <Tag>
                                {formatLabelMap[
                                  normalizePostTypeFormat(tag.format)
                                ] || "Meta Ads (5 headlines + 5 primary texts)"}
                              </Tag>
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </Space>
              </Card>
            ),
          },
        ]}
      />
      <Modal
        title="Edit Tag"
        open={!!editingTag}
        onCancel={closeEditTag}
        onOk={handleSaveTagEdit}
        okText="Save"
        okButtonProps={{ disabled: !canEditKnowledge }}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Input
            placeholder="Tag name"
            value={editTagName}
            onChange={(event) => setEditTagName(event.target.value)}
          />
          <Input.TextArea
            rows={3}
            placeholder="Tag description"
            value={editTagDescription}
            onChange={(event) => setEditTagDescription(event.target.value)}
          />
        </Space>
      </Modal>
      <Modal
        title="Edit Post Type"
        open={!!editingPostTypeTag}
        onCancel={closeEditPostTypeTag}
        onOk={handleSavePostTypeTagEdit}
        okText="Save"
        okButtonProps={{ disabled: !canEditKnowledge }}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Input
            placeholder="Post type name"
            value={editPostTypeTagName}
            onChange={(event) => setEditPostTypeTagName(event.target.value)}
            disabled={!canEditKnowledge}
          />
          <Input.TextArea
            rows={3}
            placeholder="Post type description"
            value={editPostTypeTagDescription}
            onChange={(event) =>
              setEditPostTypeTagDescription(event.target.value)
            }
            disabled={!canEditKnowledge}
          />
          <Input.TextArea
            rows={2}
            placeholder="Extra info"
            value={editPostTypeTagExtraInfo}
            onChange={(event) =>
              setEditPostTypeTagExtraInfo(event.target.value)
            }
            disabled={!canEditKnowledge}
          />
          <Select
            value={editPostTypeTagFormat}
            onChange={setEditPostTypeTagFormat}
            options={postTypeFormatOptions}
            style={{ width: "100%" }}
            disabled={!canEditKnowledge}
          />
          <div
            ref={postTypeEditDocsPasteRef}
            tabIndex={0}
            className="paste-upload-area"
          >
            <Upload
              accept=".pdf,.txt,.md,.json,.csv,.doc,.docx"
              multiple
              showUploadList={false}
              disabled={!isWorkspaceReady || !canEditKnowledge}
              beforeUpload={(file) => {
                handleAddPostTypeTagEditDoc(file);
                return false;
              }}
            >
              <Button
                icon={<UploadOutlined />}
                disabled={!isWorkspaceReady || !canEditKnowledge}
              >
                Add References
              </Button>
            </Upload>
          </div>
          {editPostTypeTagDocs.length > 0 ? (
            <List
              size="small"
              bordered
              dataSource={editPostTypeTagDocs}
              renderItem={(doc) => (
                <List.Item
                  actions={[
                    <Button
                      key="remove"
                      type="link"
                      onClick={() => handleRemovePostTypeTagEditDoc(doc.id)}
                      disabled={!canEditKnowledge}
                    >
                      Remove
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={doc.name}
                    description={
                      doc.size
                        ? `${formatBytes(doc.size)} • ${doc.mimeType || "file"}`
                        : doc.mimeType || "file"
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Text type="secondary">No references yet.</Text>
          )}
        </Space>
      </Modal>
    </Space>
  );

  function renderPostExamplesTab() {
    const postTypeOptions = [
      { value: "", label: "All post types" },
      ...postTypeTags.map((tag) => ({
        value: tag.id,
        label: tag.name,
      })),
    ];
    const resolvePostTypeLabel = (id) =>
      postTypeTags.find((tag) => tag.id === id)?.name || "All post types";
    const postExampleTagLabelMap = POST_EXAMPLE_TAG_OPTIONS.reduce(
      (acc, item) => {
        acc[item.value] = item.label;
        return acc;
      },
      {},
    );
    const resolvePostExampleTagLabel = (value) =>
      postExampleTagLabelMap[value] || value;

    return (
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Tooltip title="Post examples are used to guide post generation layouts and composition. Attach real examples so the AI can mirror structure, pacing, and visual style.">
          <Text type="secondary">
            Post examples guide post generation by showcasing layouts, pacing,
            and visual style to emulate.
          </Text>
        </Tooltip>
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Input
            placeholder="Folder name (e.g. Native Story references)"
            value={postExampleName}
            onChange={(event) => setPostExampleName(event.target.value)}
            disabled={!canEditKnowledge}
          />
          <Input.TextArea
            rows={3}
            placeholder="Notes on what makes these examples work"
            value={postExampleNotes}
            onChange={(event) => setPostExampleNotes(event.target.value)}
            disabled={!canEditKnowledge}
          />
          <Select
            value={postExampleTypeId}
            onChange={setPostExampleTypeId}
            options={postTypeOptions}
            placeholder="Post type (optional)"
            style={{ width: "100%" }}
            disabled={!canEditKnowledge}
          />
          <Select
            mode="tags"
            value={postExampleTags}
            onChange={setPostExampleTags}
            options={POST_EXAMPLE_TAG_OPTIONS}
            placeholder="Performance tags (optional)"
            maxTagCount="responsive"
            style={{ width: "100%" }}
            disabled={!canEditKnowledge}
          />
          <div
            ref={postExamplePasteRef}
            tabIndex={0}
            className="paste-upload-area"
          >
            <Upload
              accept="image/*,video/*"
              multiple
              showUploadList={false}
              disabled={!isWorkspaceReady || !canEditKnowledge}
              beforeUpload={(file) => {
                handleAddPostExampleImage(file);
                return false;
              }}
            >
              <Button
                icon={<UploadOutlined />}
                disabled={!isWorkspaceReady || !canEditKnowledge}
              >
                Add Example Media
              </Button>
            </Upload>
          </div>
          <Text type="secondary">
            Videos are analyzed into context JSON only. Original video files
            are not stored.
          </Text>
          {postExampleImages.length > 0 && (
            <Space size={8} wrap>
              {postExampleImages.map((img) => (
                <div
                  key={img.id}
                  style={{
                    border: "1px solid #f0f0f0",
                    borderRadius: 6,
                    padding: 4,
                    width: 64,
                    textAlign: "center",
                  }}
                >
                  {renderPostExamplePreview(img, { height: 48 })}
                  <Input
                    size="small"
                    placeholder="Shot name"
                    value={img.name}
                    onChange={(event) =>
                      handlePostExampleImageName(img.id, event.target.value)
                    }
                    disabled={!canEditKnowledge}
                  />
                  <Button
                    size="small"
                    type="link"
                    onClick={() => handleRemovePostExampleImage(img.id)}
                    disabled={!canEditKnowledge}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </Space>
          )}
          <Button
            type="primary"
            icon={<FolderAddOutlined />}
            onClick={handleAddPostExampleFolder}
            disabled={!canEditKnowledge}
            block
          >
            Add Post Example Folder
          </Button>
        </Space>
        <List
          bordered
          dataSource={postExamples}
          locale={{ emptyText: "No post example folders yet." }}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Tooltip key="edit" title="Edit folder">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => openEditPostExampleFolder(item)}
                    disabled={!canEditKnowledge}
                  />
                </Tooltip>,
                <Popconfirm
                  key="delete"
                  title="Delete this folder?"
                  onConfirm={() => handleDeletePostExampleFolder(item.id)}
                  okText="Delete"
                  cancelText="Cancel"
                  disabled={!canEditKnowledge}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!canEditKnowledge}
                  />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space size={8} wrap>
                    <span>{item.name}</span>
                    <Tag>{resolvePostTypeLabel(item.postTypeId)}</Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={4}>
                    <Text type="secondary">{item.notes || "No notes"}</Text>
                    {item.tags && item.tags.length > 0 ? (
                      <Space size={6} wrap>
                        {item.tags.map((tag) => (
                          <Tag key={tag} icon={<TagOutlined />}>
                            {resolvePostExampleTagLabel(tag)}
                          </Tag>
                        ))}
                      </Space>
                    ) : null}
                    {item.images && item.images.length > 0 ? (
                      <Space size={8} wrap>
                        {item.images.map((img) => (
                          <div
                            key={img.id}
                            style={{
                              border: "1px solid #f0f0f0",
                              borderRadius: 6,
                              padding: 4,
                              width: 84,
                              textAlign: "center",
                            }}
                          >
                            {renderPostExamplePreview(img, { height: 48 })}
                            <Text
                              style={{
                                fontSize: 11,
                                maxWidth: 72,
                                display: "block",
                              }}
                              ellipsis={{ tooltip: img.name || "Shot" }}
                            >
                              {img.name || "Shot"}
                            </Text>
                          </div>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary">No media yet.</Text>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
        <Modal
          title="Edit Post Examples"
          open={!!editingPostExampleFolder}
          onCancel={closeEditPostExampleFolder}
          onOk={handleSavePostExampleEdit}
          okText="Save"
          okButtonProps={{ disabled: !canEditKnowledge }}
        >
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Input
              placeholder="Folder name"
              value={editPostExampleName}
              onChange={(event) => setEditPostExampleName(event.target.value)}
              disabled={!canEditKnowledge}
            />
            <Input.TextArea
              rows={3}
              placeholder="Folder notes"
              value={editPostExampleNotes}
              onChange={(event) => setEditPostExampleNotes(event.target.value)}
              disabled={!canEditKnowledge}
            />
            <Select
              value={editPostExampleTypeId}
              onChange={setEditPostExampleTypeId}
              options={postTypeOptions}
              style={{ width: "100%" }}
              disabled={!canEditKnowledge}
            />
            <Select
              mode="tags"
              value={editPostExampleTags}
              onChange={setEditPostExampleTags}
              options={POST_EXAMPLE_TAG_OPTIONS}
              placeholder="Performance tags (optional)"
              maxTagCount="responsive"
              style={{ width: "100%" }}
              disabled={!canEditKnowledge}
            />
            <div
              ref={postExampleEditPasteRef}
              tabIndex={0}
              className="paste-upload-area"
            >
              <Upload
                accept="image/*,video/*"
                multiple
                showUploadList={false}
                disabled={!isWorkspaceReady || !canEditKnowledge}
                beforeUpload={(file) => {
                  handleAddPostExampleEditImage(file);
                  return false;
                }}
              >
                <Button
                  icon={<UploadOutlined />}
                  disabled={!isWorkspaceReady || !canEditKnowledge}
                >
                  Add Media
                </Button>
              </Upload>
            </div>
            {editPostExampleImages.length > 0 ? (
              <Space size={8} wrap>
                {editPostExampleImages.map((img) => (
                  <div
                    key={img.id}
                    style={{
                      border: "1px solid #f0f0f0",
                      borderRadius: 6,
                      padding: 4,
                      width: 120,
                    }}
                  >
                    {renderPostExamplePreview(img, { height: 64 })}
                    <Input
                      size="small"
                      placeholder="Shot name"
                      value={img.name}
                      onChange={(event) =>
                        handleEditPostExampleImageName(
                          img.id,
                          event.target.value,
                        )
                      }
                      disabled={!canEditKnowledge}
                    />
                    <Button
                      size="small"
                      type="link"
                      onClick={() => handleRemovePostExampleEditImage(img.id)}
                      disabled={!canEditKnowledge}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </Space>
            ) : (
              <Text type="secondary">No media yet.</Text>
            )}
          </Space>
        </Modal>
      </Space>
    );
  }

  const handleIntegrationConnect = (provider) => {
    if (provider === "Google Drive") {
      const clientId = import.meta?.env?.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        messageApi.info("Add VITE_GOOGLE_CLIENT_ID to your .env to enable Google Drive.");
        return;
      }
      const redirectUri = import.meta?.env?.VITE_GOOGLE_DRIVE_REDIRECT_URI || "http://localhost:5173/auth/google/drive-callback";
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/userinfo.email",
        access_type: "offline",
        prompt: "consent",
      });
      const driveUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
      window.location.href = driveUrl;
      return;
    }
    messageApi.info(`${provider} integration is coming soon.`);
  };

  const integrationsTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <LinkOutlined /> Integrations
        </Title>
        <Text type="secondary">
          Connect external tools to import assets and export creatives.
        </Text>
      </div>
      <List
        bordered
        dataSource={[
          {
            key: "Google Drive",
            title: "Google Drive",
            description: "Export generated videos, images, and creatives directly to your Drive.",
            connected: googleDriveConnected,
          },
          {
            key: "Meta",
            title: "Meta",
            description: "Sync ad copy and upload creatives to Ads Manager.",
            connected: false,
          },
          {
            key: "Slack",
            title: "Slack",
            description: "Share previews and approvals with your team.",
            connected: false,
          },
        ]}
        renderItem={(item) => (
          <List.Item
            actions={[
              item.connected ? (
                <Space key="connected" size={6}>
                  <Tag color="green" icon={<CheckCircleOutlined />}>Connected</Tag>
                  <Button
                    key="disconnect"
                    size="small"
                    danger
                    onClick={() => onGoogleDriveDisconnect?.()}
                  >
                    Disconnect
                  </Button>
                </Space>
              ) : (
                <Button
                  key="connect"
                  type="primary"
                  onClick={() => handleIntegrationConnect(item.key)}
                >
                  Connect
                </Button>
              ),
            ]}
          >
            <List.Item.Meta
              title={<Space size={6}>{item.title}</Space>}
              description={item.description}
            />
          </List.Item>
        )}
      />
    </Space>
  );

  const storageTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <HddOutlined /> Storage
        </Title>
        <Text type="secondary">
          Shows approximate storage usage for your workspace assets and
          database-backed settings.
        </Text>
      </div>
      <List
        size="small"
        bordered
        dataSource={[
          {
            label: "Knowledge images (Supabase Storage)",
            value: `${formatBytes(storageStats.imageBytes)} • ${storageStats.imageCount} image(s)`,
          },
          {
            label: "Analysis references (Supabase Storage)",
            value: `${formatBytes(storageStats.analysisBytes)} • ${storageStats.analysisCount} file(s)`,
          },
          {
            label: "Ad copy references (Supabase Storage)",
            value: `${formatBytes(storageStats.adCopyBytes)} • ${storageStats.adCopyCount} file(s)`,
          },
          {
            label: "Blog references (Supabase Storage)",
            value: `${formatBytes(storageStats.blogBytes)} • ${storageStats.blogCount} file(s)`,
          },
          {
            label: "Brand assets (Supabase Storage)",
            value: `${formatBytes(storageStats.brandBytes)} • ${storageStats.brandCount} asset(s)`,
          },
          {
            label: "User settings (Supabase DB)",
            value: "Synced",
          },
        ]}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta title={item.label} description={item.value} />
          </List.Item>
        )}
      />
    </Space>
  );

  const libraryTabGroup = (
    <Tabs
      className="settings-subtabs"
      size="small"
      items={[
        { key: "brand", label: "Brand", children: brandTab },
        { key: "knowledge", label: "Knowledge", children: knowledgeTab },
        { key: "tags", label: "Tags", children: tagsTab },
      ]}
    />
  );

  const workspaceHealthItems = [
    {
      label: "Active workspace",
      value: currentWorkspace
        ? `${currentWorkspace.name || "Workspace"} • ${currentWorkspace.role}`
        : "None selected",
    },
    {
      label: "Members loaded",
      value: workspaceMembers.length
        ? `${workspaceMembers.length} member(s)`
        : "No members loaded",
    },
    {
      label: "Last workspace sync",
      value: syncStatus?.lastWorkspaceSyncAt
        ? new Date(syncStatus.lastWorkspaceSyncAt).toLocaleString()
        : "No sync yet",
    },
    {
      label: "Shared activity feedback",
      value: activityPreferences?.length
        ? `${activityPreferences.length} item(s)`
        : "None yet",
    },
  ];

  const workspaceSharedItems = [
    {
      label: "AI instructions + reference URLs",
      detail: "Shared with all workspace members.",
    },
    {
      label: "Knowledge folders, tags, and post types",
      detail: "Shared library visible in all generators.",
    },
    {
      label: "Brand assets + post example libraries",
      detail: "Shared reference images for your team.",
    },
    {
      label: "Analysis + ad copy references",
      detail: "Docs synced to the workspace knowledge base.",
    },
    {
      label: "Activity feedback (likes/dislikes)",
      detail: "Shared preferences used to improve outputs.",
    },
  ];

  const healthCategories = healthReport.length
    ? [...new Set(healthReport.map((r) => r.category).filter((c) => c !== "Summary"))]
    : [];
  const healthSummary = healthReport.find((r) => r.category === "Summary");

  const healthTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <HddOutlined /> Health
        </Title>
        <Text type="secondary">
          Tests all API endpoints, storage connections, and services with
          round-trip verification.
        </Text>
      </div>
      <Button onClick={runHealthCheck} loading={healthLoading} type="primary">
        Run Health Check
      </Button>
      {healthSummary && (
        <Alert
          type={healthSummary.ok ? "success" : "warning"}
          showIcon
          message={healthSummary.ok ? "All checks passed" : "Some checks failed"}
          description={healthSummary.detail}
        />
      )}
      {healthCategories.map((category) => (
        <Card
          key={category}
          size="small"
          title={category}
          className="panel-card"
        >
          <List
            size="small"
            dataSource={healthReport.filter((r) => r.category === category)}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  title={
                    <Space size="small">
                      <Tag color={item.ok ? "green" : "red"}>
                        {item.ok ? "OK" : "FAIL"}
                      </Tag>
                      <span>{item.label}</span>
                    </Space>
                  }
                  description={
                    <Text
                      type={item.ok ? "secondary" : "danger"}
                      style={{ fontSize: 12 }}
                    >
                      {item.detail}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      ))}
      {healthReport.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="No results yet"
          description="Click Run Health Check to test all endpoints and connections."
        />
      )}
      <Card size="small" className="panel-card">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text strong>Workspace Health</Text>
          <Text type="secondary">
            Confirms shared data is syncing for the active workspace.
          </Text>
          {!isWorkspaceReady && (
            <Alert
              type="warning"
              showIcon
              message="No active workspace"
              description="Select a workspace to validate shared data."
            />
          )}
          {syncStatus?.lastError ? (
            <Alert
              type="warning"
              showIcon
              message="Workspace sync warning"
              description={syncStatus.lastError}
            />
          ) : null}
          <List
            size="small"
            bordered
            dataSource={workspaceHealthItems}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta title={item.label} description={item.value} />
              </List.Item>
            )}
          />
        </Space>
      </Card>
    </Space>
  );

  const activityTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <DatabaseOutlined /> Activity
        </Title>
        <Text type="secondary">
          Liked and disliked creatives used to shape AI preferences.
        </Text>
      </div>
      <Button onClick={refreshActivityLibrary}>Refresh</Button>
      <Tabs
        size="small"
        items={[
          {
            key: "likes",
            label: `Likes (${activityLibrary.filter((item) => item.sentiment === "like").length})`,
            children: (
              <div className="activity-grid">
                {activityLibrary
                  .filter((item) => item.sentiment === "like")
                  .map((item) => (
                    <div
                      key={item.id}
                      className="activity-card"
                      onClick={() => setPreviewItem(item)}
                    >
                      {item.mediaUrl || item.url ? (
                        item.mediaType === "video" ? (
                          <video
                            src={item.mediaUrl || item.url}
                            className="activity-media"
                            controls
                          />
                        ) : (
                          <img
                            src={item.mediaUrl || item.url}
                            alt="Liked creative"
                            className="activity-media"
                          />
                        )
                      ) : (
                        <div className="activity-media activity-media--empty">
                          No preview
                        </div>
                      )}
                      <div className="activity-actions">
                        <Button
                          size="small"
                          shape="circle"
                          icon={<LikeFilled />}
                          className="activity-action activity-action--active"
                          onClick={(event) => event.stopPropagation()}
                        />
                        <Button
                          size="small"
                          shape="circle"
                          icon={<DislikeOutlined />}
                          className="activity-action"
                          onClick={(event) => event.stopPropagation()}
                        />
                        <Button
                          size="small"
                          shape="circle"
                          icon={<CloudDownloadOutlined />}
                          className="activity-action"
                          onClick={(event) => event.stopPropagation()}
                        />
                      </div>
                    </div>
                  ))}
                {activityLibrary.filter((item) => item.sentiment === "like")
                  .length === 0 && (
                  <Text type="secondary">No liked creatives yet.</Text>
                )}
              </div>
            ),
          },
          {
            key: "dislikes",
            label: `Dislikes (${activityLibrary.filter((item) => item.sentiment === "dislike").length})`,
            children: (
              <div className="activity-grid">
                {activityLibrary
                  .filter((item) => item.sentiment === "dislike")
                  .map((item) => (
                    <div
                      key={item.id}
                      className="activity-card"
                      onClick={() => setPreviewItem(item)}
                    >
                      {item.mediaUrl || item.url ? (
                        item.mediaType === "video" ? (
                          <video
                            src={item.mediaUrl || item.url}
                            className="activity-media"
                            controls
                          />
                        ) : (
                          <img
                            src={item.mediaUrl || item.url}
                            alt="Disliked creative"
                            className="activity-media"
                          />
                        )
                      ) : (
                        <div className="activity-media activity-media--empty">
                          No preview
                        </div>
                      )}
                      <div className="activity-actions">
                        <Button
                          size="small"
                          shape="circle"
                          icon={<LikeOutlined />}
                          className="activity-action"
                          onClick={(event) => event.stopPropagation()}
                        />
                        <Button
                          size="small"
                          shape="circle"
                          icon={<DislikeFilled />}
                          className="activity-action activity-action--active"
                          onClick={(event) => event.stopPropagation()}
                        />
                        <Button
                          size="small"
                          shape="circle"
                          icon={<CloudDownloadOutlined />}
                          className="activity-action"
                          onClick={(event) => event.stopPropagation()}
                        />
                      </div>
                    </div>
                  ))}
                {activityLibrary.filter((item) => item.sentiment === "dislike")
                  .length === 0 && (
                  <Text type="secondary">No disliked creatives yet.</Text>
                )}
              </div>
            ),
          },
        ]}
      />
      <Modal
        open={!!previewItem}
        onCancel={() => setPreviewItem(null)}
        footer={null}
        width={760}
        centered
        title={null}
      >
        {previewItem?.mediaUrl || previewItem?.url ? (
          previewItem.mediaType === "video" ? (
            <video
              src={previewItem.mediaUrl || previewItem.url}
              controls
              style={{ width: "100%", maxHeight: "70vh", borderRadius: 12 }}
            />
          ) : (
            <img
              src={previewItem.mediaUrl || previewItem.url}
              alt="Preview"
              style={{
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 12,
              }}
            />
          )
        ) : (
          <Text type="secondary">No preview available.</Text>
        )}
      </Modal>
    </Space>
  );

  const fastestAnalysis = aggregateFastest(modelStatsState.analysis);
  const fastestVariants = aggregateFastest(modelStatsState.variants);
  const fastestImages = aggregateFastest(modelStatsState.images);
  const mostErrors = aggregateErrors();
  const { bestLike, bestDislike } = aggregatePreferences();

  const statsTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <RobotOutlined /> Model Stats
        </Title>
        <Text type="secondary">
          Tracks speed and outcomes by model for analysis, variants, and image
          generation.
        </Text>
      </div>
      <Button onClick={refreshModelStats}>Refresh Stats</Button>
      <List
        size="small"
        bordered
        dataSource={[
          {
            label: "Fastest analysis model (avg)",
            value: fastestAnalysis
              ? `${fastestAnalysis.model} • ${Math.round(
                  fastestAnalysis.avgMs,
                )} ms over ${fastestAnalysis.count} run(s)`
              : "No data yet",
          },
          {
            label: "Fastest variants model (avg)",
            value: fastestVariants
              ? `${fastestVariants.model} • ${Math.round(
                  fastestVariants.avgMs,
                )} ms over ${fastestVariants.count} run(s)`
              : "No data yet",
          },
          {
            label: "Fastest image model (avg)",
            value: fastestImages
              ? `${fastestImages.model} • ${Math.round(
                  fastestImages.avgMs,
                )} ms over ${fastestImages.count} run(s)`
              : "No data yet",
          },
          {
            label: "Most likes",
            value: bestLike
              ? `${bestLike.model} • ${bestLike.count} like(s)`
              : "No data yet",
          },
          {
            label: "Most dislikes",
            value: bestDislike
              ? `${bestDislike.model} • ${bestDislike.count} dislike(s)`
              : "No data yet",
          },
          {
            label: "Most errors (all categories)",
            value: mostErrors
              ? `${mostErrors.model} • ${mostErrors.count} error(s)`
              : "No data yet",
          },
        ]}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta title={item.label} description={item.value} />
          </List.Item>
        )}
      />
    </Space>
  );

  const aiTabGroup = (
    <Tabs
      className="settings-subtabs"
      size="small"
      items={[{ key: "ai_profile", label: "AI Profile", children: aiTab }]}
    />
  );

  const usageTabGroup = (
    <Tabs
      className="settings-subtabs"
      size="small"
      items={[
        { key: "activity", label: "Activity", children: activityTab },
        { key: "storage", label: "Storage", children: storageTab },
        { key: "stats", label: "Stats", children: statsTab },
      ]}
    />
  );

  const systemTabGroup = (
    <Tabs
      className="settings-subtabs"
      size="small"
      items={[
        { key: "health", label: "Health", children: healthTab },
        {
          key: "connections",
          label: "Connections",
          children: (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <Title level={4} style={{ marginBottom: 4 }}>
                  <LinkOutlined /> Connection Settings
                </Title>
                <Text type="secondary">
                  Shows where each knowledge/input is used. This is a static map
                  of intended usage, not a live verification.
                </Text>
              </div>
              <List
                size="small"
                bordered
                dataSource={[
                  {
                    label: "Brand (colors, fonts, assets)",
                    detail: "Used in image generation + video prompt building.",
                  },
                  {
                    label: "Knowledge Base • Creative",
                    detail: "Used in image/video generation prompts.",
                  },
                  {
                    label: "Knowledge Base • Analysis",
                    detail: "Used in analysis prompt guidance.",
                  },
                  {
                    label: "Knowledge Base • Copy",
                    detail:
                      "Used in image/video prompts and variants generation.",
                  },
                  {
                    label: "Tags",
                    detail: "Injected into image/video generation prompts.",
                  },
                  {
                    label: "Special Instructions",
                    detail: "Injected into image/video generation prompts.",
                  },
                  {
                    label: "AI Profile",
                    detail: "Injected into analysis + generation prompts.",
                  },
                  {
                    label: "Ad Copy References",
                    detail: "Injected into image/video prompts and variants.",
                  },
                  {
                    label: "Activity Feedback (Likes/Dislikes)",
                    detail:
                      "Used in post copy prompts to reinforce winning patterns and avoid disliked output.",
                  },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.label}
                      description={item.detail}
                    />
                  </List.Item>
                )}
              />
            </Space>
          ),
        },
        {
          key: "integrations",
          label: "Integrations",
          children: integrationsTab,
        },
        { key: "api", label: "API Keys", children: apiTab },
      ]}
    />
  );

  const workspacesTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <TeamOutlined /> Workspaces
        </Title>
        <Text type="secondary">
          Manage shared libraries, members, and permissions. Knowledge sync runs
          automatically within the active workspace.
        </Text>
        {!isBusinessTier && (
          <Alert
            type="info"
            showIcon
            message="Business plan feature"
            description="Team workspaces, invites, and permissions are available on the Business plan."
            style={{ marginTop: 12 }}
          />
        )}
        {!canEditKnowledge && (
          <Alert
            type="info"
            showIcon
            message="Read-only workspace access"
            description="You can browse shared libraries but cannot edit them."
            style={{ marginTop: 12 }}
          />
        )}
      </div>
      <Card size="small" className="panel-card">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text strong>Active workspace</Text>
          <Select
            value={activeWorkspaceId}
            loading={workspaceLoading}
            onChange={handleSetActiveWorkspace}
            options={workspaceList.map((item) => ({
              value: item.id,
              label: `${item.name} (${item.isPersonal ? "personal" : "team"} • ${item.role})`,
            }))}
            placeholder="Select workspace"
          />
          <Text type="secondary">
            Active workspace controls which shared library and members are shown
            here.
          </Text>
        </Space>
      </Card>

      <Card size="small" className="panel-card">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text strong>Workspace Diagnostics</Text>
          <Text type="secondary">
            Fix workspace membership issues automatically. This will ensure you have proper access to all your workspaces.
          </Text>
          <Button
            type="default"
            icon={<SettingOutlined />}
            onClick={async () => {
              try {
                const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
                const response = await fetch(`${apiBaseUrl}/api/workspaces/diagnose-and-fix`, {
                  method: 'POST',
                  credentials: 'include'
                });
                const data = await response.json();
                if (!response.ok) {
                  throw new Error(data.error || 'Failed to run diagnostics');
                }
                if (data.fixed) {
                  messageApi.success(`Fixed ${data.issues_fixed || 0} workspace issue(s)`);
                  // Refresh workspace list
                  await fetchWorkspaces();
                } else {
                  messageApi.info('No workspace issues found');
                }
              } catch (error) {
                const errorData = error?.response ? await error.response.json() : { message: error?.message || "Failed to run diagnostics" };
                showWorkspaceError(messageApi, errorData);
              }
            }}
          >
            Fix Workspace Issues
          </Button>
        </Space>
      </Card>

      {isBusinessTier ? (
        <>
          <Card size="small" className="panel-card">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Text strong>Workspace settings</Text>
              {workspaceSettingsDraft.isPersonal ? (
                <Alert
                  type="info"
                  showIcon
                  message="Personal workspace"
                  description="Personal workspaces are private and do not support team join settings."
                />
              ) : null}
              <Space direction="vertical" size="small">
                <Space align="center" wrap>
                  <Switch
                    checked={workspaceSettingsDraft.domainJoinEnabled}
                    onChange={handleToggleDomainJoin}
                    disabled={
                      !canManageWorkspace || workspaceSettingsDraft.isPersonal
                    }
                  />
                  <Text>
                    Allow users with this email domain to join automatically
                  </Text>
                </Space>
                <Input
                  placeholder="example.com"
                  value={workspaceSettingsDraft.domainJoinDomain}
                  onChange={(event) =>
                    setWorkspaceSettingsDraft((prev) => ({
                      ...prev,
                      domainJoinDomain: event.target.value,
                    }))
                  }
                  disabled={
                    !canManageWorkspace ||
                    !workspaceSettingsDraft.domainJoinEnabled ||
                    workspaceSettingsDraft.isPersonal
                  }
                  style={{ maxWidth: 280 }}
                />
                <Text type="secondary">
                  Members who sign up with a matching domain can auto-join this
                  team.
                </Text>
              </Space>
              <Button
                type="primary"
                onClick={handleSaveWorkspaceSettings}
                loading={workspaceSettingsSaving}
                disabled={!canManageWorkspace}
              >
                Save workspace settings
              </Button>
            </Space>
          </Card>

          <Card size="small" className="panel-card">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Text strong>Privacy &amp; sharing</Text>
              <Text type="secondary">
                Everything saved in this workspace is shared with members.
                Owners can fine-tune who can manage permissions and spend
                limits.
              </Text>
              <List
                size="small"
                bordered
                dataSource={workspaceSharedItems}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.label}
                      description={item.detail}
                    />
                  </List.Item>
                )}
              />
              <Divider style={{ margin: "8px 0" }} />
              <Space align="center" wrap>
                <Switch
                  checked={workspaceSettingsDraft.allowAdminManagePermissions}
                  onChange={(value) =>
                    setWorkspaceSettingsDraft((prev) => ({
                      ...prev,
                      allowAdminManagePermissions: value,
                    }))
                  }
                  disabled={!isWorkspaceOwner}
                />
                <Text>Allow admins to manage member permissions</Text>
              </Space>
              {!isWorkspaceOwner && (
                <Text type="secondary">
                  Only workspace owners can adjust these controls.
                </Text>
              )}
              <Button
                type="primary"
                onClick={handleSaveWorkspaceSettings}
                loading={workspaceSettingsSaving}
                disabled={!canManageWorkspace}
              >
                Save privacy settings
              </Button>
            </Space>
          </Card>

          <Card size="small" className="panel-card">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Text strong>Create new workspace</Text>
              <Space wrap>
                <Input
                  placeholder="Workspace name"
                  value={newWorkspaceName}
                  onChange={(event) => setNewWorkspaceName(event.target.value)}
                  style={{ minWidth: 240 }}
                />
                <Button
                  icon={<PlusOutlined />}
                  type="primary"
                  onClick={handleCreateWorkspace}
                >
                  Create workspace
                </Button>
              </Space>
            </Space>
          </Card>

          <Card size="small" className="panel-card">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Text strong>Invite members</Text>
              <Text type="secondary">
                Invites require approval from an owner or admin. New members
                will see the shared knowledge library once approved.
              </Text>
              <Space wrap>
                <Input
                  placeholder="Email address"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  style={{ minWidth: 240 }}
                  disabled={!canInviteMembers}
                />
                <Select
                  value={inviteRole}
                  onChange={setInviteRole}
                  options={roleOptions.filter((item) => item.value !== "owner")}
                  style={{ minWidth: 160 }}
                  disabled={!canInviteMembers}
                />
                <Button
                  icon={<UserAddOutlined />}
                  type="primary"
                  onClick={handleInviteMember}
                  disabled={!canInviteMembers}
                >
                  Send invite
                </Button>
              </Space>
              {!canInviteMembers && (
                <Text type="secondary">
                  You do not have permission to invite members.
                </Text>
              )}
            </Space>
          </Card>

          <Card size="small" className="panel-card">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Text strong>Members</Text>
              <List
                dataSource={workspaceMembers}
                locale={{ emptyText: "No members found." }}
                renderItem={(member) => {
                  const profile = member.profiles || {};
                  const isOwner = member.role === "owner";
                  return (
                    <List.Item
                      actions={[
                        canManagePermissions && !isOwner ? (
                          <Select
                            key="role"
                            size="small"
                            value={member.role}
                            onChange={(value) =>
                              handleUpdateMemberRole(member.id, value)
                            }
                            options={memberRoleOptions}
                            style={{ minWidth: 120 }}
                          />
                        ) : (
                          <Tag
                            key="role"
                            color={member.role === "owner" ? "gold" : "blue"}
                          >
                            {member.role || "member"}
                          </Tag>
                        ),
                        canEditMemberSettings && !isOwner ? (
                          <Button
                            key="permissions"
                            size="small"
                            icon={<SettingOutlined />}
                            onClick={() => openMemberPermissions(member)}
                          >
                            Permissions
                          </Button>
                        ) : null,
                        canManageMembers && !isOwner ? (
                          <Popconfirm
                            key="remove"
                            title="Remove this member?"
                            onConfirm={() => handleRemoveMember(member.id)}
                            okText="Remove"
                            cancelText="Cancel"
                          >
                            <Button danger size="small">
                              Remove
                            </Button>
                          </Popconfirm>
                        ) : null,
                      ].filter(Boolean)}
                    >
                      <List.Item.Meta
                        title={
                          <Space size="small">
                            {profile.name || profile.email || "Member"}
                            {isOwner && <CrownOutlined />}
                          </Space>
                        }
                        description={
                          <Text type="secondary">
                            {profile.email || "Member"}
                            {member.role && rolePermissions[member.role]
                              ? ` • ${rolePermissions[member.role]}`
                              : ""}
                          </Text>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </Space>
          </Card>

          <Card size="small" className="panel-card">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Text strong>Invites</Text>
              <List
                dataSource={workspaceInvites.filter(
                  (invite) => invite.status === "pending",
                )}
                locale={{ emptyText: "No pending invites." }}
                renderItem={(invite) => (
                  <List.Item
                    actions={[
                      invite.status === "pending" && canInviteMembers ? (
                        <Button
                          key="revoke"
                          size="small"
                          onClick={() => handleRevokeInvite(invite.id)}
                        >
                          Revoke
                        </Button>
                      ) : null,
                    ].filter(Boolean)}
                  >
                    <List.Item.Meta
                      title={`${invite.email} (${invite.role || "viewer"})`}
                      description={
                        <Space size="small" wrap>
                          <Tag color="blue">Pending</Tag>
                          <Text type="secondary">
                            Sent{" "}
                            {invite.created_at
                              ? new Date(invite.created_at).toLocaleString()
                              : ""}
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Space>
          </Card>

          {isWorkspaceOwner && (
            <Card size="small" className="panel-card">
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Text strong style={{ color: '#cf1322' }}>⚠️ Danger Zone</Text>
                <Alert
                  type="warning"
                  showIcon
                  message="Destructive Actions"
                  description="These actions are permanent and cannot be undone. Please proceed with caution."
                />
                <Divider style={{ margin: "8px 0" }} />
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <div>
                    <Text strong>Export Workspace Data</Text>
                    <div>
                      <Text type="secondary">
                        Download all workspace data as a JSON file for backup or migration purposes.
                      </Text>
                    </div>
                  </div>
                  <Button
                    icon={<CloudDownloadOutlined />}
                    onClick={handleExportWorkspaceData}
                    disabled={!activeWorkspaceId}
                  >
                    Export All Data
                  </Button>
                </Space>
                <Divider style={{ margin: "8px 0" }} />
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <div>
                    <Text strong>Delete Workspace</Text>
                    <div>
                      <Text type="secondary">
                        Permanently delete this workspace and all associated data. This action cannot be undone.
                      </Text>
                    </div>
                  </div>
                  <Popconfirm
                    title="Are you absolutely sure?"
                    description="This will permanently delete the workspace and all its data. Export your data first if needed."
                    onConfirm={handleDeleteWorkspace}
                    okText="Yes, delete workspace"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      disabled={!activeWorkspaceId}
                    >
                      Delete Workspace
                    </Button>
                  </Popconfirm>
                </Space>
              </Space>
            </Card>
          )}

          <Modal
            title="Member access"
            open={permissionsModalOpen}
            onCancel={handleClosePermissionsModal}
            onOk={handleSaveMemberPermissions}
            okText="Save"
            okButtonProps={{
              loading: permissionsSaving,
              disabled:
                !permissionsTarget || permissionsTarget?.role === "owner",
            }}
            cancelButtonProps={{ disabled: permissionsSaving }}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <Text strong>
                  {permissionsTarget?.profiles?.name ||
                    permissionsTarget?.profiles?.email ||
                    permissionsTarget?.user_id ||
                    "Member"}
                </Text>
                <div>
                  <Text type="secondary">
                    {permissionsTarget?.profiles?.email || "No email on file"}
                  </Text>
                </div>
              </div>
              <Divider style={{ margin: "4px 0" }} />
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                <Text strong>Permissions</Text>
                {permissionCatalog.map((item) => (
                  <Space
                    key={item.key}
                    align="center"
                    style={{
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <Text>{item.label}</Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.detail}
                        </Text>
                      </div>
                    </div>
                    <Switch
                      checked={Boolean(permissionsDraft[item.key])}
                      onChange={(value) =>
                        handlePermissionToggle(item.key, value)
                      }
                      disabled={
                        !canManagePermissions ||
                        permissionsTarget?.role === "owner"
                      }
                    />
                  </Space>
                ))}
              </Space>
              {!canManagePermissions && (
                <Alert
                  type="info"
                  showIcon
                  message="You do not have permission to update member access."
                />
              )}
            </Space>
          </Modal>
        </>
      ) : null}
    </Space>
  );

  const notificationsTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          <BellOutlined /> Notifications
        </Title>
        <Text type="secondary">
          Track workspace invites, updates, and system activity.
        </Text>
      </div>
      <Card size="small" className="panel-card">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text strong>Generation alerts</Text>
          <Space
            align="center"
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <div>
              <Text>Play a sound when generation completes</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Queues a short tone when analysis or generation finishes.
                </Text>
              </div>
            </div>
            <Switch
              checked={Boolean(resolvedNotificationPreferences.completionAudio)}
              onChange={handleToggleCompletionAudio}
            />
          </Space>
          <Space
            align="center"
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <div>
              <Text>Desktop push notification</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Browser notification when generation is done.
                </Text>
              </div>
            </div>
            <Switch
              checked={Boolean(resolvedNotificationPreferences.completionPush)}
              onChange={handleToggleCompletionPush}
              disabled={!notificationSupport}
            />
          </Space>
          <Space align="center" wrap>
            <Text type="secondary">Browser permission:</Text>
            <Tag
              color={
                effectiveNotificationPermission === "granted"
                  ? "green"
                  : effectiveNotificationPermission === "denied"
                    ? "red"
                    : effectiveNotificationPermission === "unsupported"
                      ? "default"
                      : "gold"
              }
            >
              {effectiveNotificationPermission === "unsupported"
                ? "unsupported"
                : effectiveNotificationPermission}
            </Tag>
            <Button
              size="small"
              onClick={() => handleToggleCompletionPush(true)}
              disabled={!notificationSupport}
            >
              Request permission
            </Button>
          </Space>
        </Space>
      </Card>
      <Card size="small" className="panel-card">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text strong>Workspace invites</Text>
          {pendingInvitesForUser.length > 0 ? (
            <List
              size="small"
              bordered
              dataSource={pendingInvitesForUser}
              renderItem={(invite) => (
                <List.Item
                  actions={[
                    <Button
                      key="accept"
                      type="primary"
                      size="small"
                      onClick={() => handleAcceptInvite(invite.id)}
                    >
                      Accept
                    </Button>,
                    <Button
                      key="reject"
                      size="small"
                      onClick={() => handleRejectInvite(invite.id)}
                    >
                      Reject
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={invite.workspaces?.name || "Workspace"}
                    description={`Role: ${invite.role || "viewer"}`}
                  />
                </List.Item>
              )}
            />
          ) : (
            <Text type="secondary">No pending invites.</Text>
          )}
        </Space>
      </Card>
      <Card size="small" className="panel-card">
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text strong>Workspace updates</Text>
          <Text type="secondary">
            Active workspace:{" "}
            {currentWorkspace
              ? `${currentWorkspace.name || "Workspace"} • ${currentWorkspace.role}`
              : "None selected"}
          </Text>
          <Text type="secondary">
            Last workspace sync:{" "}
            {syncStatus?.lastWorkspaceSyncAt
              ? new Date(syncStatus.lastWorkspaceSyncAt).toLocaleString()
              : "No sync yet"}
          </Text>
          {syncStatus?.lastError ? (
            <Alert
              type="warning"
              showIcon
              message="Recent sync issue"
              description={syncStatus.lastError}
            />
          ) : null}
        </Space>
      </Card>
    </Space>
  );

  const isDisabled = Boolean(disabled);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {contextHolder}
      {isDisabled && (
        <Alert
          type="warning"
          showIcon
          message="Settings locked"
          description="Generation is running. Cancel or wait to edit settings."
          style={{ marginBottom: 12 }}
        />
      )}
      {pendingInvitesForUser.length > 0 && (
        <Alert
          type="info"
          showIcon
          closable
          message={`You have ${pendingInvitesForUser.length} pending workspace invite${pendingInvitesForUser.length === 1 ? '' : 's'}`}
          description={
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {pendingInvitesForUser.map(invite => (
                <div key={invite.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div>
                    <strong>{invite.workspaces?.name || "Workspace"}</strong>{" "}
                    <Tag
                      color={
                        invite.role === "owner"
                          ? "gold"
                          : invite.role === "admin"
                            ? "blue"
                            : invite.role === "editor"
                              ? "green"
                              : "default"
                      }
                    >
                      {invite.role}
                    </Tag>
                  </div>
                  <Space size="small">
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleAcceptInvite(invite.id)}
                    >
                      Accept
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleRejectInvite(invite.id)}
                    >
                      Reject
                    </Button>
                  </Space>
                </div>
              ))}
            </Space>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      <div
        style={{
          height: "100%",
          opacity: isDisabled ? 0.6 : 1,
          pointerEvents: isDisabled ? "none" : "auto",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <Tabs
          className="settings-tabs settings-tabs--left"
          tabPosition="left"
          style={{ height: "100%" }}
          items={[
            {
              key: "ai",
              label: (
                <span className="settings-nav-label">
                  <RobotOutlined />
                  <span className="settings-nav-text">AI</span>
                </span>
              ),
              children: aiTabGroup,
            },
            {
              key: "preferences",
              label: (
                <span className="settings-nav-label">
                  <SettingOutlined />
                  <span className="settings-nav-text">Preferences</span>
                </span>
              ),
              children: preferencesTab,
            },
            {
              key: "library",
              label: (
                <span className="settings-nav-label">
                  <AppstoreOutlined />
                  <span className="settings-nav-text">Library</span>
                </span>
              ),
              children: libraryTabGroup,
            },
            {
              key: "workspaces",
              label: (
                <span className="settings-nav-label">
                  <TeamOutlined />
                  <span className="settings-nav-text">Workspaces</span>
                </span>
              ),
              children: workspacesTab,
            },
            {
              key: "notifications",
              label: (
                <span className="settings-nav-label">
                  <BellOutlined />
                  <span className="settings-nav-text">Notifications</span>
                </span>
              ),
              children: notificationsTab,
            },
            {
              key: "usage",
              label: (
                <span className="settings-nav-label">
                  <DatabaseOutlined />
                  <span className="settings-nav-text">Usage</span>
                </span>
              ),
              children: usageTabGroup,
            },
            {
              key: "system",
              label: (
                <span className="settings-nav-label">
                  <HddOutlined />
                  <span className="settings-nav-text">System</span>
                </span>
              ),
              children: systemTabGroup,
            },
          ]}
        />
        <div className="settings-footer">
          <span>Settings help: use Health to validate connections.</span>
          <span>Workspace data is stored in your workspace database. Preferences save per user.</span>
          <span>
            Sync:{" "}
            {syncStatus?.settingsSaving || syncStatus?.workspaceSaving
              ? "Saving…"
              : "Idle"}
            {syncStatus?.lastSettingsSyncAt
              ? ` • Settings ${new Date(syncStatus.lastSettingsSyncAt).toLocaleTimeString()}`
              : ""}
            {syncStatus?.lastWorkspaceSyncAt
              ? ` • Workspace ${new Date(syncStatus.lastWorkspaceSyncAt).toLocaleTimeString()}`
              : ""}
            {syncStatus?.lastError ? ` • Error: ${syncStatus.lastError}` : ""}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
